import React, { useState, useEffect } from 'react';
import { BoardMeetingRegister, GeneralMeetingRegister, CommitteeMeetingRegister, ChargesRegister, ContractsRegister, TransfersRegister } from './Registers';
import { getEntityProfile, isLLPType } from '../../constants/mcaEntity';
import { PartnersMeetings } from './llp/LlpRegister';
import { ToastProvider, useToast } from '../../components/Toast';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
// All 31 Registrar of Companies offices under the MCA, per the Feb 16,
// 2026 reorganization (Delhi/Haryana/UP/Mumbai/Kolkata each split into
// separate ROCs by jurisdiction) — a fixed, known list, so ROC is a
// dropdown rather than free text (an "Other…" escape hatch still exists
// below it for anything not covered here). Alphabetical by label.
const ROC_OPTIONS = [
  'ROC Ahmedabad', 'ROC Bangalore', 'ROC Chandigarh', 'ROC Chennai',
  'ROC Chhattisgarh (Bilaspur)', 'ROC Coimbatore', 'ROC Cuttack',
  'ROC NCT of Delhi-I', 'ROC NCT of Delhi-II', 'ROC Ernakulam', 'ROC Goa',
  'ROC Guwahati', 'ROC Gwalior', 'ROC Haryana', 'ROC Himachal Pradesh',
  'ROC Hyderabad', 'ROC Jaipur', 'ROC Jammu', 'ROC Kanpur (Uttar Pradesh-I)',
  'ROC Kolkata-I', 'ROC Kolkata-II', 'ROC Mumbai-I', 'ROC Mumbai-II',
  'ROC Nagpur', 'ROC Noida', 'ROC Patna', 'ROC Puducherry', 'ROC Pune',
  'ROC Ranchi', 'ROC Shillong', 'ROC Uttarakhand (Dehradun)',
];
const FILING_STATUS_COLORS = {
  'Draft':       'bg-gray-100 text-gray-600',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Filed':       'bg-orange-100 text-orange-700',
  'Approved':    'bg-green-100 text-green-700',
  'Synced':      'bg-emerald-100 text-emerald-700',
  'Rejected':    'bg-red-100 text-red-700',
};
// ── Compliance-health stat helpers — pure functions over already-fetched
// arrays (annualFilings/eventFilings/directors/generalMeetings), so they
// don't need to know how that data was loaded. ──
function computeOpenFilingsCount(annualFilings, eventFilings) {
  const openAnnual = (annualFilings || []).filter(af => af.status !== 'Synced' && af.status !== 'Approved').length;
  const openEvent = (eventFilings || []).filter(f => f.status !== 'Approved' && f.status !== 'Synced').length;
  return openAnnual + openEvent;
}
function computeNextDueDate(annualFilings) {
  const dates = (annualFilings || [])
    .flatMap(af => Array.isArray(af.filings) ? af.filings : [])
    .filter(f => f.status !== 'Approved' && f.due_date)
    .map(f => f.due_date)
    .sort();
  return dates[0] || null;
}
function computeDscExpiring(directors) {
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return (directors || []).filter(d => {
    if (!d.dsc_expiry || d.is_active === false) return false;
    const exp = new Date(d.dsc_expiry);
    return !Number.isNaN(exp.getTime()) && exp <= in30;
  }).length;
}
function computeDinKycPending(directors) {
  return (directors || []).filter(d => d.is_active !== false && d.din_kyc_status && d.din_kyc_status !== 'Done').length;
}
// DIR-3 KYC is triennial (once per 3 financial years) effective 31 Mar
// 2026, Companies (Registration Offices & Fees) Amendment Rules, 2026 —
// last-filed FY "2025-26" -> next due FY "2028-29". Purely derived, never
// stored, so it can't drift from din_kyc_year.
function computeNextDueFy(lastFiledFy) {
  const m = (lastFiledFy || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const startY = Number(m[1]) + 3;
  return `${startY}-${String((startY + 1) % 100).padStart(2, '0')}`;
}
function isKycDueSoon(lastFiledFy) {
  const nextDue = computeNextDueFy(lastFiledFy);
  if (!nextDue) return false;
  const nextDueStartY = Number(nextDue.slice(0, 4));
  const now = new Date();
  // FY runs Apr-Mar; treat Jan-Mar as still within the previous FY start-year.
  const currentFyStartY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return currentFyStartY >= nextDueStartY;
}
function computeLastAgmDate(generalMeetings) {
  const agmDates = (generalMeetings || [])
    .filter(m => (m.meeting_type || '').toUpperCase().includes('AGM'))
    .map(m => m.meeting_date)
    .filter(Boolean)
    .sort();
  return agmDates.length ? agmDates[agmDates.length - 1] : null;
}
// Latest financial year for which an LLP annual-return (Form 11) case exists.
function computeLastLlpReturnFy(annualFilings) {
  const fys = (annualFilings || [])
    .filter(af => af.entity_regime === 'LLP')
    .map(af => af.financial_year)
    .filter(Boolean)
    .sort();
  return fys.length ? fys[fys.length - 1] : null;
}
// Section heading used inside the Add/Edit Director modal — coloured
// accent bar + uppercase title + optional hint line.
function DirSection({ title, hint, accent = '#00206E', children }) {
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-1">
        <span className="w-1 h-4 rounded-full" style={{ background: accent }} />
        <p className="text-[13px] font-bold tracking-wide text-[#0f1f45] dark:text-slate-100">{title}</p>
      </div>
      {hint && <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3 ml-3.5">{hint}</p>}
      {!hint && <div className="mb-3" />}
      <div className="ml-3.5">{children}</div>
    </section>
  );
}

function CompanyMaster({ onNavigate }) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const { showToast, showConfirm } = useToast();

const [showForm, setShowForm] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [persons, setPersons] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [shareholders, setShareholders] = useState([]);
  const [shareholdersLoading, setShareholdersLoading] = useState(false);
  const [filings, setFilings] = useState([]);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [annualFilings, setAnnualFilings] = useState([]);
  const [annualFilingsLoading, setAnnualFilingsLoading] = useState(false);
  const [generalMeetings, setGeneralMeetings] = useState([]);
  const [showShareholderForm, setShowShareholderForm] = useState(false);
  const [editingShareholderId, setEditingShareholderId] = useState(null);
  const [savingShareholder, setSavingShareholder] = useState(false);

  const EMPTY_SH = {
    person_id: '',
    member_name: '',
    father_husband_name: '',
    address: '',
    email: '',
    pan: '',
    aadhaar: '',
    folio_number: '',
    share_class: 'Equity',
    distinctive_from: '',
    distinctive_to: '',
    no_of_shares: '',
    face_value: '10',
    amount_paid: '10',
    mode_of_acquisition: 'Subscription to Memorandum',
    date_of_allotment: '',
    date_of_entry: '',
    date_of_cessation: '',
    cessation_reason: '',
    transferred_to: '',
    is_active: true,
    notes: '',
    director_ref_id: '',
    contribution_form: 'Cash',
  };

  const [shForm, setShForm] = useState(EMPTY_SH);
  // Register of Members "Add Entry" source: manual | people | director
  const [shSource, setShSource] = useState('manual');
  // LLP Capital Contribution — per-partner inline edits { partnerId: {obligation,received,profit} }
  const [contribEdits, setContribEdits] = useState({});
  const [contribSavingId, setContribSavingId] = useState(null);

  // ── DIRECTORS STATE ──
  const [directors, setDirectors] = useState([]);
  const [directorsLoading, setDirectorsLoading] = useState(false);
  const [showDirectorForm, setShowDirectorForm] = useState(false);
  const [editingDirectorId, setEditingDirectorId] = useState(null);
  const [savingDirector, setSavingDirector] = useState(false);
  const [importablePeople, setImportablePeople] = useState([]);
  const [showImportSuggestion, setShowImportSuggestion] = useState(false);
  const [importing, setImporting] = useState(false);

  // ── AUDITORS STATE (Statutory Auditor Master — see mca_company_auditors) ──
  const [auditors, setAuditors] = useState([]);
  const [auditorsLoading, setAuditorsLoading] = useState(false);
  const [showAuditorForm, setShowAuditorForm] = useState(false);
  const [editingAuditorId, setEditingAuditorId] = useState(null);
  const [savingAuditor, setSavingAuditor] = useState(false);

  // Address sub-shape reused for the firm's registered address, the
  // signing auditor's personal address, and each joint auditor's address —
  // matches e-Form ADT-1's Address Line 1/2, Country, Pin Code, Area/
  // Locality, City, District, State/UT, Email ID block exactly.
  const EMPTY_AUDITOR_ADDRESS = { line1: '', line2: '', country: 'India', pincode: '', area: '', city: '', district: '', state: '', email: '' };
  // One joint auditor entry — same category/firm/individual shape as the
  // primary auditor below (ADT-1 4(c)-4(h) repeats per joint auditor).
  const EMPTY_JOINT_AUDITOR = {
    category: 'Firm', frn: '', firm_name: '', firm_pan: '',
    signing_membership_no: '', auditor_name: '', auditor_pan: '', auditor_email: '',
  };
  // One row of the Rule 6 "prior tenure" table — ADT-1 4(m)-4(s), disclosing
  // every earlier stint this auditor/firm/member had at this company
  // (excluding a break of 5+ years) so the appointment's own Rule 6/Sec
  // 139(2) eligibility can be checked.
  const EMPTY_PRIOR_TENURE_ROW = { s_no: '', frn: '', membership_no: '', fy_start: '', fy_end: '' };

  const EMPTY_ADT1_DETAILS = {
    section139_2_applicable: '',
    nature_of_appointment: '',
    nature_of_appointment_other: '',
    appointed_in_agm: '',
    agm_date: '',
    is_joint_auditor: false,
    num_auditors_appointed: 1,
    auditor_category: 'Firm',
    firm_pan: '',
    firm_address: EMPTY_AUDITOR_ADDRESS,
    signing_membership_no: '',
    auditor_name: '',
    auditor_pan: '',
    auditor_address: EMPTY_AUDITOR_ADDRESS,
    auditor_email: '',
    auditor_mobile: '',
    period_from: '',
    period_to: '',
    within_20_company_limit: '',
    prior_association: '',
    prior_tenure: [],
    audit_committee_recommendation: '',
    tribunal_order_srn: '',
    casual_vacancy: { reason: '', reason_other: '', srn: '', date: '', vacated_frn: '', vacated_membership_no: '', reasons_text: '' },
    joint_auditors: [],
  };

  const EMPTY_AUDITOR = {
    firm_name: '',
    frn: '',
    peer_review_no: '',
    appointment_category: 'Regular Appointment',
    appointed_by: 'AGM',
    confirmation_status: 'Confirmed',
    appointment_date: '',
    board_resolution_date: '',
    agm_resolution_date: '',
    appointed_agm_serial_label: '',
    term_to_year: '',
    audit_fee: '',
    is_active: true,
    cessation_date: '',
    cessation_reason: '',
    cessation_resolution_date: '',
    form_adt1_filed: false,
    form_adt1_srn: '',
    form_adt1_date: '',
    notes: '',
    adt1_details: EMPTY_ADT1_DETAILS,
  };
  const [auditorForm, setAuditorForm] = useState(EMPTY_AUDITOR);
  // Draft rows for the repeatable Joint Auditor / Prior Tenure panels —
  // same "draft + add + list" UX as Directors' Other Directorships panel.
  const [jointAuditorDraft, setJointAuditorDraft] = useState(EMPTY_JOINT_AUDITOR);
  const [priorTenureDraft, setPriorTenureDraft] = useState(EMPTY_PRIOR_TENURE_ROW);

  // Number of Indian financial years (April–March) a period spans — ADT-1
  // 4(j) "Number of financial year(s) to which appointment relates" is
  // meant to be counted FY-block-wise, not as a raw date difference (e.g.
  // 10-Dec-2025 to 31-Mar-2026 is 1 FY, not "~4 months").
  const fyStartYear = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  };
  const computeFyCount = (fromStr, toStr) => {
    const fromY = fyStartYear(fromStr), toY = fyStartYear(toStr);
    if (fromY == null || toY == null || toY < fromY) return null;
    return toY - fromY + 1;
  };

  const EMPTY_DIR = {
    person_id: '',
    director_name: '',
    din: '',
    dpin: '',
    pan: '',
    aadhaar: '',
    dsc_expiry: '',
    date_of_birth: '',
    father_name: '',
    place_of_birth: '',
    nationality: 'Indian',
    occupation: '',
    education_qualification: '',
    address_aadhaar: '',
    email: '',
    mobile: '',
    secondary_mobile: '',
    other_directorships: [],
    designation: 'Director',
    appointment_date: '',
    board_resolution_date: '',
    agm_resolution_date: '',
    form_dir12_filed: false,
    form_dir12_srn: '',
    form_dir12_date: '',
    din_kyc_status: 'Pending',
    din_kyc_year: '',
    din_status: 'Active',
    cessation_date: '',
    cessation_reason: '',
    cessation_resolution_date: '',
    form_dir12_cessation_filed: false,
    form_dir12_cessation_srn: '',
    is_active: true,
    notes: '',
    is_independent_director: false,
    appointed_at_incorporation: false,
    // ── LLP (partner) fields ──
    is_designated_partner: false,
    partner_type: 'Individual',
    date_admitted: '',
    contribution_obligation: '',
    contribution_received: '',
    profit_share_percent: '',
    form4_appointment_srn: '',
    form4_appointment_date: '',
    form4_cessation_srn: '',
    form4_cessation_date: '',
  };

  const [dirForm, setDirForm] = useState(EMPTY_DIR);
  // Draft row for the "Other Directorships / Interests" repeatable panel
  const EMPTY_OD = { cin: '', company_name: '', designation: '', shareholding_percent: '', shareholding_amount: '' };
  const [odDraft, setOdDraft] = useState(EMPTY_OD);
  const [odOpen, setOdOpen] = useState(false);

  // Entry mode: 'client' = pick an ERP client, 'form' = edit company fields.
  // Manual (no-client) entry was removed — every company must map to a client.
  const [entryMode, setEntryMode] = useState('client');
  const [syncingPeople, setSyncingPeople] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [autoFetching, setAutoFetching] = useState(false);
  const [showAllClients, setShowAllClients] = useState(false);
  const [clientDropOpen, setClientDropOpen] = useState(false);

  const MCA_CLIENT_TYPES = [
    'Private Limited', 'Public Limited', 'LLP',
    'One Person Company', 'Section 8 Company',
    'Producer Company', 'Nidhi Company'
  ];

  const CLIENT_TYPE_TO_COMPANY_TYPE = {
    'Private Limited': 'Private Limited Company',
    'Public Limited': 'Public Limited Company',
    'LLP': 'LLP',
    'One Person Company': 'One Person Company',
    'Section 8 Company': 'Section 8 Company',
    'Producer Company': 'Producer Company',
    'Nidhi Company': 'Nidhi Company',
  };

  // Normalizes a client_type string for resilient matching — handles casing,
  // spacing, punctuation and common naming variations from the main ERP
  // (e.g. "Pvt Ltd", "Private Limited Company", "OPC", "opc", etc.)
  const normalizeType = (val) =>
    String(val || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const TYPE_SYNONYMS = {
    [normalizeType('Private Limited')]: 'Private Limited',
    [normalizeType('Private Limited Company')]: 'Private Limited',
    [normalizeType('Pvt Ltd')]: 'Private Limited',
    [normalizeType('Pvt Ltd Company')]: 'Private Limited',
    [normalizeType('Public Limited')]: 'Public Limited',
    [normalizeType('Public Limited Company')]: 'Public Limited',
    [normalizeType('Ltd')]: 'Public Limited',
    [normalizeType('LLP')]: 'LLP',
    [normalizeType('Limited Liability Partnership')]: 'LLP',
    [normalizeType('One Person Company')]: 'One Person Company',
    [normalizeType('OPC')]: 'One Person Company',
    [normalizeType('Section 8 Company')]: 'Section 8 Company',
    [normalizeType('Section 8')]: 'Section 8 Company',
    [normalizeType('Sec 8 Company')]: 'Section 8 Company',
    [normalizeType('Producer Company')]: 'Producer Company',
    [normalizeType('Nidhi Company')]: 'Nidhi Company',
    [normalizeType('Nidhi')]: 'Nidhi Company',
  };

  // Resolves any raw client_type string (from the ERP) to a canonical
  // MCA_CLIENT_TYPES value, or null if it truly doesn't match any known type
  const resolveMcaType = (rawType) => TYPE_SYNONYMS[normalizeType(rawType)] || null;

  const EMPTY_FORM = {
    client_id: '',
    cin_llpin: '',
    company_name: '',
    company_type: 'Private Limited Company',
    roc: '',
    pan: '',
    tan: '',
    gstin: '',
    esic: '',
    epfo: '',
    gst_registrations: [],
    incorporation_date: '',
    authorized_capital: '',
    paid_up_capital: '',
    llp_contribution: '',
    date_of_agreement: '',
    form3_srn: '',
    form3_date: '',
    registered_office: '',
    email: '',
    mobile: '',
    is_listed: false,
    has_subsidiaries: false,
  };

  const [form, setForm] = useState(EMPTY_FORM);
  const [gstDraft, setGstDraft] = useState({ state: '', gstin: '' });
  const addGstReg = () => {
    if (!gstDraft.gstin.trim()) return;
    setForm(f => ({ ...f, gst_registrations: [...(f.gst_registrations || []), { ...gstDraft, id: Date.now() }] }));
    setGstDraft({ state: '', gstin: '' });
  };
  const removeGstReg = (id) => setForm(f => ({ ...f, gst_registrations: (f.gst_registrations || []).filter(x => x.id !== id) }));

  useEffect(() => {
    const h = (e) => {
      if (!e.target.closest('[data-kebab]')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${BASE}/api/mca/companies`, { headers });
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Company fetch error:', e);
      setCompanies([]);
    }
  };

  // Compliance-health stat cards need directors/filings/annualFilings/
  // generalMeetings as soon as a company is opened — not only once the
  // matching tab has been clicked (the older per-tab fetches below still
  // exist unchanged for that click-to-refresh behavior; this just also
  // primes the same state eagerly so the stat row isn't blank/stale).
  useEffect(() => {
    if (!selectedCompany?.id) return;
    const cid = selectedCompany.id;
    fetch(`${BASE}/api/mca/companies/${cid}/directors`, { headers })
      .then(r => r.json()).then(data => setDirectors(Array.isArray(data) ? data : []))
      .catch(() => setDirectors([]));
    fetch(`${BASE}/api/mca/event-filings?company_id=${cid}`, { headers })
      .then(r => r.json()).then(data => setFilings(Array.isArray(data) ? data : []))
      .catch(() => setFilings([]));
    fetch(`${BASE}/api/mca/companies/${cid}/general-meetings`, { headers })
      .then(r => r.json()).then(data => setGeneralMeetings(Array.isArray(data) ? data : []))
      .catch(() => setGeneralMeetings([]));
    setAnnualFilingsLoading(true);
    fetch(`${BASE}/api/mca/annual-filings?company_id=${cid}`, { headers })
      .then(r => r.json()).then(data => setAnnualFilings(Array.isArray(data) ? data : []))
      .catch(() => setAnnualFilings([]))
      .finally(() => setAnnualFilingsLoading(false));
  }, [selectedCompany?.id]);

useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/clients`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/persons`, { headers }).then(r => r.json()).catch(() => []),
    ]).then(([clientData, personData]) => {
      setClients(Array.isArray(clientData) ? clientData : []);
      setPersons(Array.isArray(personData) ? personData : []);
    });
    fetchCompanies();
  }, []);

  // Auto-fetch client details + linked directors when client selected
  const handleClientSelect = async (client) => {
    setAutoFetching(true);
    try {
      // Fetch persons linked to this client
      const res = await fetch(
        `${BASE}/api/persons?client_id=${client.id}`,
        { headers }
      );
      const linkedPersons = await res.json();
      const personList = Array.isArray(linkedPersons) ? linkedPersons : [];

      // Map client_type to MCA company_type (via resilient resolver)
      const companyType = CLIENT_TYPE_TO_COMPANY_TYPE[resolveMcaType(client.client_type)]
        || 'Private Limited Company';

      // Build address from client fields
      const address = [client.address, client.city, client.state, client.pincode]
        .filter(Boolean).join(', ');

      setForm({
        ...EMPTY_FORM,
        client_id: client.id,
        company_name: client.client_name || '',
        company_type: companyType,
        pan: client.pan || '',
        gstin: client.gstin || '',
        incorporation_date: client.date_of_incorporation
          ? String(client.date_of_incorporation).slice(0, 10)
          : '',
        registered_office: address,
        email: client.email || '',
        mobile: client.mobile || '',
      });

      // Merge linked persons into persons list if not already present
      setPersons(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newOnes = personList.filter(p => !existingIds.has(p.id));
        return [...prev, ...newOnes];
      });

    } catch (e) {
      console.error('Auto-fetch client details error:', e);
      // Still set basic details even if persons fetch fails
      setForm(f => ({
        ...f,
        client_id: client.id,
        company_name: client.client_name || '',
        company_type: CLIENT_TYPE_TO_COMPANY_TYPE[client.client_type] || 'Private Limited Company',
        pan: client.pan || '',
        gstin: client.gstin || '',
        email: client.email || '',
        mobile: client.mobile || '',
      }));
    } finally {
      setAutoFetching(false);
      setEntryMode('form');
    }
  };

  const isLLP = isLLPType(form.company_type);
  const ic = "w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00206E] focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40 dark:focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 transition";

  const mcaClients = clients.filter(c => resolveMcaType(c.client_type) !== null);
  // Fallback: if the resilient matcher still finds zero MCA-type clients but
  // the ERP does have clients, let the user browse all of them rather than
  // being blocked entirely (e.g. if client_type field uses an unmapped label)
  const clientPool = (mcaClients.length > 0 || showAllClients) ? (showAllClients ? clients : mcaClients) : mcaClients;
  const filteredMcaClients = clientPool.filter(c => {
    const q = clientSearch.toLowerCase();
    return !q ||
      c.client_name?.toLowerCase().includes(q) ||
      c.file_number?.toLowerCase().includes(q) ||
      c.pan?.toLowerCase().includes(q);
  });

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setEntryMode('client');
    setClientSearch('');
    setShowForm(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const selectedClient = clients.find(c => String(c.id) === String(form.client_id));
      const payload = {
        ...form,
        client_name: selectedClient?.client_name || form.company_name,
      };

      const url = editingId
        ? `${BASE}/api/mca/companies/${editingId}`
        : `${BASE}/api/mca/companies`;

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.existing_id) {
          const existing = companies.find(c => String(c.id) === String(data.existing_id));
          if (existing && await showConfirm(`${data.message}.\n\nOpen the existing company record?`, { title: 'Company already exists', confirmLabel: 'Open record', confirmColor: '#0f1f45' })) {
            resetForm();
            setSelectedCompany(existing);
          } else {
            showToast(data.message, 'error');
          }
          return;
        }
        showToast(data.message || 'Could not save company', 'error');
        return;
      }

      await fetchCompanies();
      resetForm();
      showToast(editingId ? 'Company updated.' : 'Company added.', 'success');
    } catch (e) {
      console.error('Company save error:', e);
      showToast('Server error while saving company', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c) => {
    setForm({
      client_id: c.client_id || '',
      cin_llpin: c.cin_llpin || '',
      company_name: c.company_name || '',
      company_type: c.company_type || 'Private Limited Company',
      roc: c.roc || '',
      pan: c.pan || '',
      tan: c.tan || '',
      gstin: c.gstin || '',
      esic: c.esic || '',
      epfo: c.epfo || '',
      gst_registrations: Array.isArray(c.gst_registrations) ? c.gst_registrations : [],
      incorporation_date: c.incorporation_date
        ? String(c.incorporation_date).slice(0, 10) : '',
      authorized_capital: c.authorized_capital || '',
      paid_up_capital: c.paid_up_capital || '',
      llp_contribution: c.llp_contribution || '',
      date_of_agreement: c.date_of_agreement ? String(c.date_of_agreement).slice(0, 10) : '',
      form3_srn: c.form3_srn || '',
      form3_date: c.form3_date ? String(c.form3_date).slice(0, 10) : '',
      registered_office: c.registered_office || '',
      email: c.email || '',
      mobile: c.mobile || '',
      is_listed: !!c.is_listed,
      has_subsidiaries: !!c.has_subsidiaries,
    });
    setEditingId(c.id);
    setEntryMode('form');
    setShowForm(true);
  };

  const handleDelete = async (c) => {
    if (!await showConfirm(`Delete company "${c.company_name}"? This cannot be undone.`, { title: 'Delete company', confirmLabel: 'Delete' })) return;
    try {
      const res = await fetch(`${BASE}/api/mca/companies/${c.id}`, {
        method: 'DELETE', headers,
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.message || 'Could not delete company', 'error'); return; }
      await fetchCompanies();
      showToast('Company deleted.', 'success');
    } catch (e) {
      showToast('Server error while deleting company', 'error');
    }
  };

  const filteredCompanies = companies.filter(c => {
    const q = search.toLowerCase();
    return !q ||
      c.company_name?.toLowerCase().includes(q) ||
      c.client_name?.toLowerCase().includes(q) ||
      c.cin_llpin?.toLowerCase().includes(q) ||
      c.pan?.toLowerCase().includes(q);
  });

if (selectedCompany) {
    // Entity framework — one source of truth for LLP vs company terminology,
    // tab set and compliance rules. `E.isLLP` replaces the old ad-hoc checks.
    const E = getEntityProfile(selectedCompany.company_type);
    const isLLPView = E.isLLP;

    // ── DIRECTOR FUNCTIONS ──
    const fetchDirectors = async () => {
      setDirectorsLoading(true);
      try {
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/directors`,
          { headers }
        );
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setDirectors(list);

        // Check if client has linked people in People module not yet imported.
        // GET /api/persons returns a `linked_clients` array per person (each
        // { client_id, role, is_active }), never a flat p.role — match on that.
        if (selectedCompany.client_id) {
          const existingPersonIds = list.map(d => d.person_id).filter(Boolean);
          const linked = persons.filter(p =>
            !existingPersonIds.includes(p.id) &&
            Array.isArray(p.linked_clients) &&
            p.linked_clients.some(l =>
              String(l.client_id) === String(selectedCompany.client_id) &&
              l.is_active !== false &&
              (isLLPView
                ? (l.role === 'Partner' || l.role === 'Designated Partner')
                : (l.role === 'Director' || l.role === 'Designated Partner'))
            )
          );
          setImportablePeople(linked);
          setShowImportSuggestion(linked.length > 0);
        }
      } catch (e) {
        setDirectors([]);
      } finally {
        setDirectorsLoading(false);
      }
    };

    // ── AUDITOR FUNCTIONS ──
    const fetchAuditors = async () => {
      setAuditorsLoading(true);
      try {
        const res = await fetch(`${BASE}/api/mca/companies/${selectedCompany.id}/auditors`, { headers });
        const data = await res.json();
        setAuditors(Array.isArray(data) ? data : []);
      } catch (e) {
        setAuditors([]);
      } finally {
        setAuditorsLoading(false);
      }
    };

    const handleSaveAuditor = async (e) => {
      e.preventDefault();
      if (!auditorForm.firm_name) { showToast('Enter the auditor firm/name first', 'error'); return; }
      setSavingAuditor(true);
      try {
        const url = editingAuditorId
          ? `${BASE}/api/mca/companies/${selectedCompany.id}/auditors/${editingAuditorId}`
          : `${BASE}/api/mca/companies/${selectedCompany.id}/auditors`;
        const res = await fetch(url, {
          method: editingAuditorId ? 'PUT' : 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(auditorForm),
        });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Could not save auditor', 'error'); return; }
        await fetchAuditors();
        setAuditorForm(EMPTY_AUDITOR);
        setEditingAuditorId(null);
        setShowAuditorForm(false);
        showToast(editingAuditorId ? 'Auditor updated.' : 'Auditor saved.', 'success');
      } catch (e) {
        showToast('Server error while saving auditor', 'error');
      } finally {
        setSavingAuditor(false);
      }
    };

    const handleEditAuditor = (a) => {
      setAuditorForm({
        firm_name: a.firm_name || '',
        frn: a.frn || '',
        peer_review_no: a.peer_review_no || '',
        appointment_category: a.appointment_category || 'Regular Appointment',
        appointed_by: a.appointed_by || 'AGM',
        confirmation_status: a.confirmation_status || 'Confirmed',
        appointment_date: a.appointment_date ? String(a.appointment_date).slice(0, 10) : '',
        board_resolution_date: a.board_resolution_date ? String(a.board_resolution_date).slice(0, 10) : '',
        agm_resolution_date: a.agm_resolution_date ? String(a.agm_resolution_date).slice(0, 10) : '',
        appointed_agm_serial_label: a.appointed_agm_serial_label || '',
        term_to_year: a.term_to_year || '',
        audit_fee: a.audit_fee ?? '',
        is_active: a.is_active !== false,
        cessation_date: a.cessation_date ? String(a.cessation_date).slice(0, 10) : '',
        cessation_reason: a.cessation_reason || '',
        cessation_resolution_date: a.cessation_resolution_date ? String(a.cessation_resolution_date).slice(0, 10) : '',
        form_adt1_filed: !!a.form_adt1_filed,
        form_adt1_srn: a.form_adt1_srn || '',
        form_adt1_date: a.form_adt1_date ? String(a.form_adt1_date).slice(0, 10) : '',
        notes: a.notes || '',
        // Merge onto the empty defaults so an older record saved before a
        // field existed (or before adt1_details existed at all) still
        // renders every input instead of crashing on a missing nested key.
        adt1_details: {
          ...EMPTY_ADT1_DETAILS,
          ...(a.adt1_details || {}),
          firm_address: { ...EMPTY_AUDITOR_ADDRESS, ...(a.adt1_details?.firm_address || {}) },
          auditor_address: { ...EMPTY_AUDITOR_ADDRESS, ...(a.adt1_details?.auditor_address || {}) },
          casual_vacancy: { ...EMPTY_ADT1_DETAILS.casual_vacancy, ...(a.adt1_details?.casual_vacancy || {}) },
          prior_tenure: Array.isArray(a.adt1_details?.prior_tenure) ? a.adt1_details.prior_tenure : [],
          joint_auditors: Array.isArray(a.adt1_details?.joint_auditors) ? a.adt1_details.joint_auditors : [],
        },
      });
      setJointAuditorDraft(EMPTY_JOINT_AUDITOR);
      setPriorTenureDraft(EMPTY_PRIOR_TENURE_ROW);
      setEditingAuditorId(a.id);
      setShowAuditorForm(true);
    };

    const setAdt1 = (patch) => setAuditorForm(f => ({ ...f, adt1_details: { ...f.adt1_details, ...patch } }));

    const addJointAuditor = () => {
      if (!jointAuditorDraft.firm_name.trim() && !jointAuditorDraft.auditor_name.trim()) return;
      setAdt1({ joint_auditors: [...(auditorForm.adt1_details.joint_auditors || []), { ...jointAuditorDraft, id: Date.now() }] });
      setJointAuditorDraft(EMPTY_JOINT_AUDITOR);
    };
    const removeJointAuditor = (id) => {
      setAdt1({ joint_auditors: (auditorForm.adt1_details.joint_auditors || []).filter(x => x.id !== id) });
    };
    const addPriorTenureRow = () => {
      if (!priorTenureDraft.fy_start && !priorTenureDraft.fy_end) return;
      const rows = auditorForm.adt1_details.prior_tenure || [];
      setAdt1({ prior_tenure: [...rows, { ...priorTenureDraft, id: Date.now(), s_no: priorTenureDraft.s_no || rows.length + 1 }] });
      setPriorTenureDraft(EMPTY_PRIOR_TENURE_ROW);
    };
    const removePriorTenureRow = (id) => {
      setAdt1({ prior_tenure: (auditorForm.adt1_details.prior_tenure || []).filter(x => x.id !== id) });
    };

    const handleDeleteAuditor = async (a) => {
      if (!await showConfirm(`Remove "${a.firm_name}" from the Statutory Auditor Master?`, { title: 'Remove auditor', confirmLabel: 'Remove' })) return;
      try {
        const res = await fetch(`${BASE}/api/mca/companies/${selectedCompany.id}/auditors/${a.id}`, { method: 'DELETE', headers });
        if (!res.ok) { showToast('Could not remove auditor', 'error'); return; }
        await fetchAuditors();
        showToast('Auditor removed.', 'success');
      } catch (e) {
        showToast('Server error while removing auditor', 'error');
      }
    };

    const handleDirPersonSelect = (personId) => {
      const p = persons.find(x => String(x.id) === String(personId));
      if (!p) { setDirForm(f => ({ ...f, person_id: '' })); return; }
      setDirForm(f => ({
        ...f,
        person_id: p.id,
        director_name: p.full_name || '',
        din: p.din || '',
        dpin: p.dpin || '',
        pan: p.pan_number || '',
        aadhaar: p.aadhaar_number || '',
        dsc_expiry: p.dsc_expiry ? String(p.dsc_expiry).slice(0, 10) : '',
        date_of_birth: p.date_of_birth ? String(p.date_of_birth).slice(0, 10) : '',
        father_name: p.father_name || '',
        address_aadhaar: p.address_aadhaar || p.address_correspondence || '',
        email: p.email || '',
        mobile: p.primary_mobile || '',
        secondary_mobile: p.secondary_mobile || '',
      }));
    };

    const addOtherDirectorship = () => {
      if (!odDraft.cin.trim() && !odDraft.company_name.trim()) return;
      setDirForm(f => ({
        ...f,
        other_directorships: [...(f.other_directorships || []), { ...odDraft, id: Date.now() }],
      }));
      setOdDraft(EMPTY_OD);
    };

    const removeOtherDirectorship = (id) => {
      setDirForm(f => ({
        ...f,
        other_directorships: (f.other_directorships || []).filter(x => x.id !== id),
      }));
    };

    const handleImportFromPeople = async () => {
      if (importablePeople.length === 0) return;
      setImporting(true);
      try {
        for (const p of importablePeople) {
          const link = (p.linked_clients || []).find(l =>
            String(l.client_id) === String(selectedCompany.client_id) &&
            ['Director', 'Designated Partner', 'Partner'].includes(l.role)
          );
          const isDP = link?.role === 'Designated Partner';
          const designation = isLLPView
            ? (isDP ? 'Designated Partner' : 'Partner')
            : (isDP ? 'Designated Partner' : 'Director');
          await fetch(
            `${BASE}/api/mca/companies/${selectedCompany.id}/directors`,
            {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                person_id: p.id,
                director_name: p.full_name || '',
                din: p.din || '',
                dpin: p.dpin || '',
                pan: p.pan_number || '',
                dsc_expiry: p.dsc_expiry || null,
                email: p.email || '',
                mobile: p.primary_mobile || '',
                designation,
                is_designated_partner: isLLPView && isDP,
                din_kyc_status: 'Pending',
                is_active: true,
              }),
            }
          );
        }
        setShowImportSuggestion(false);
        setImportablePeople([]);
        await fetchDirectors();
        showToast('Imported from People Master.', 'success');
      } catch (e) {
        showToast('Error importing directors', 'error');
      } finally {
        setImporting(false);
      }
    };

    const handleSaveDirector = async (e) => {
      e.preventDefault();
      setSavingDirector(true);
      try {
        const url = editingDirectorId
          ? `${BASE}/api/mca/companies/${selectedCompany.id}/directors/${editingDirectorId}`
          : `${BASE}/api/mca/companies/${selectedCompany.id}/directors`;
        const payload = dirForm.din_status === 'Deactivated'
          ? { ...dirForm, din_kyc_status: 'Not Applicable', din_kyc_year: '' }
          : dirForm;
        const res = await fetch(url, {
          method: editingDirectorId ? 'PUT' : 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Could not save director', 'error'); return; }
        await fetchDirectors();
        setDirForm(EMPTY_DIR);
        setEditingDirectorId(null);
        setShowDirectorForm(false);
        showToast(editingDirectorId ? `${isLLPView ? 'Partner' : 'Director'} updated.` : `${isLLPView ? 'Partner' : 'Director'} saved.`, 'success');
      } catch (e) {
        showToast('Server error while saving director', 'error');
      } finally {
        setSavingDirector(false);
      }
    };

    const handleEditDirector = (d) => {
      setOdDraft(EMPTY_OD);
      setOdOpen(Array.isArray(d.other_directorships) && d.other_directorships.length > 0);
      setDirForm({
        person_id: d.person_id || '',
        director_name: d.director_name || '',
        din: d.din || '',
        dpin: d.dpin || '',
        pan: d.pan || '',
        aadhaar: d.aadhaar || '',
        dsc_expiry: d.dsc_expiry ? String(d.dsc_expiry).slice(0, 10) : '',
        date_of_birth: d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : '',
        father_name: d.father_name || '',
        place_of_birth: d.place_of_birth || '',
        nationality: d.nationality || 'Indian',
        occupation: d.occupation || '',
        education_qualification: d.education_qualification || '',
        address_aadhaar: d.address_aadhaar || '',
        email: d.email || '',
        mobile: d.mobile || '',
        secondary_mobile: d.secondary_mobile || '',
        other_directorships: Array.isArray(d.other_directorships) ? d.other_directorships : [],
        designation: d.designation || 'Director',
        appointment_date: d.appointment_date ? String(d.appointment_date).slice(0, 10) : '',
        board_resolution_date: d.board_resolution_date ? String(d.board_resolution_date).slice(0, 10) : '',
        agm_resolution_date: d.agm_resolution_date ? String(d.agm_resolution_date).slice(0, 10) : '',
        form_dir12_filed: d.form_dir12_filed || false,
        form_dir12_srn: d.form_dir12_srn || '',
        form_dir12_date: d.form_dir12_date ? String(d.form_dir12_date).slice(0, 10) : '',
        din_kyc_status: d.din_kyc_status || 'Pending',
        din_kyc_year: d.din_kyc_year || '',
        din_status: d.din_status || 'Active',
        cessation_date: d.cessation_date ? String(d.cessation_date).slice(0, 10) : '',
        cessation_reason: d.cessation_reason || '',
        cessation_resolution_date: d.cessation_resolution_date ? String(d.cessation_resolution_date).slice(0, 10) : '',
        form_dir12_cessation_filed: d.form_dir12_cessation_filed || false,
        form_dir12_cessation_srn: d.form_dir12_cessation_srn || '',
        is_active: d.is_active !== false,
        notes: d.notes || '',
        is_independent_director: d.is_independent_director || false,
        appointed_at_incorporation: d.appointed_at_incorporation || false,
        is_designated_partner: d.is_designated_partner || false,
        partner_type: d.partner_type || 'Individual',
        date_admitted: d.date_admitted ? String(d.date_admitted).slice(0, 10) : '',
        contribution_obligation: d.contribution_obligation ?? '',
        contribution_received: d.contribution_received ?? '',
        profit_share_percent: d.profit_share_percent ?? '',
        form4_appointment_srn: d.form4_appointment_srn || '',
        form4_appointment_date: d.form4_appointment_date ? String(d.form4_appointment_date).slice(0, 10) : '',
        form4_cessation_srn: d.form4_cessation_srn || '',
        form4_cessation_date: d.form4_cessation_date ? String(d.form4_cessation_date).slice(0, 10) : '',
      });
      setEditingDirectorId(d.id);
      setShowDirectorForm(true);
    };

    const handleDeleteDirector = async (d) => {
      if (!await showConfirm(`Remove "${d.director_name}" from ${isLLPView ? 'partners' : 'directors'}?`, { title: `Remove ${isLLPView ? 'partner' : 'director'}`, confirmLabel: 'Remove' })) return;
      try {
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/directors/${d.id}`,
          { method: 'DELETE', headers }
        );
        if (!res.ok) { showToast('Could not remove director', 'error'); return; }
        await fetchDirectors();
        showToast(`${isLLPView ? 'Partner' : 'Director'} removed.`, 'success');
      } catch (e) {
        showToast('Server error while removing director', 'error');
      }
    };

    const fetchShareholders = async () => {
      setShareholdersLoading(true);
      try {
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/shareholders`,
          { headers }
        );
        const data = await res.json();
        setShareholders(Array.isArray(data) ? data : []);
      } catch (e) {
        setShareholders([]);
      } finally {
        setShareholdersLoading(false);
      }
    };

    const fetchFilings = async () => {
      setFilingsLoading(true);
      try {
        const res = await fetch(
          `${BASE}/api/mca/event-filings?company_id=${selectedCompany.id}`,
          { headers }
        );
        const data = await res.json();
        setFilings(Array.isArray(data) ? data : []);
      } catch (e) {
        setFilings([]);
      } finally {
        setFilingsLoading(false);
      }
    };

    const handleShPersonSelect = (personId) => {
      const p = persons.find(x => String(x.id) === String(personId));
      if (!p) { setShForm(f => ({ ...f, person_id: '' })); return; }
      setShForm(f => ({
        ...f,
        person_id: p.id,
        member_name: p.full_name || '',
        father_husband_name: p.father_name || '',
        address: p.address_correspondence || p.address_pan || '',
        email: p.email || '',
        pan: p.pan_number || '',
        aadhaar: p.aadhaar_number || '',
      }));
    };

    // Register of Members → "Link from Director": prefill a member entry
    // from a director already on this company.
    const handleShDirectorSelect = (directorId) => {
      const d = directors.find(x => String(x.id) === String(directorId));
      if (!d) { setShForm(f => ({ ...f, director_ref_id: '' })); return; }
      // If the director is linked to a People record, prefer that person's
      // fuller data (father/address may be blank on the director row).
      const p = d.person_id ? persons.find(x => String(x.id) === String(d.person_id)) : null;
      setShForm(f => ({
        ...f,
        director_ref_id: d.id,
        person_id: d.person_id || '',
        member_name: d.director_name || p?.full_name || '',
        father_husband_name: d.father_name || p?.father_name || '',
        address: d.address_aadhaar || p?.address_aadhaar || p?.address_correspondence || '',
        email: d.email || p?.email || '',
        pan: d.pan || p?.pan_number || '',
        aadhaar: d.aadhaar || p?.aadhaar_number || '',
      }));
    };

    // Explicit push of this company's active directors + members into the
    // ERP People module (dedup by PAN). Backend endpoint is idempotent.
    const handleSyncPeople = async () => {
      setSyncingPeople(true);
      try {
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/sync-people`,
          { method: 'POST', headers }
        );
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Sync failed', 'error'); return; }
        showToast(
          (data.entity === 'LLP'
            ? `Synced ${data.partners_synced} partner(s) to People Master. `
            : `Synced ${data.directors_synced} director(s) and ${data.members_synced} member(s) to People Master. `) +
          `${data.people_created} created, ${data.people_updated} updated.`,
          'success', 5000
        );
        await fetchDirectors();
        await fetchShareholders();
      } catch (e) {
        showToast('Server error during sync', 'error');
      } finally {
        setSyncingPeople(false);
      }
    };

    // Director form → create a People Master record from the typed-in
    // details and link it, when no existing person was selected.
    const handleCreatePersonFromDirector = async () => {
      if (!dirForm.director_name) { showToast('Enter the director name first', 'error'); return; }
      try {
        const res = await fetch(`${BASE}/api/persons`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: dirForm.director_name,
            pan_number: dirForm.pan || null,
            din: dirForm.din || null,
            din_status: dirForm.din ? 'Active' : 'Not Applicable',
            dsc_expiry: dirForm.dsc_expiry || null,
            email: dirForm.email || null,
            primary_mobile: dirForm.mobile || null,
          }),
        });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Could not create person', 'error'); return; }
        setPersons(prev => [...prev, { ...data, linked_clients: [] }]);
        setDirForm(f => ({ ...f, person_id: data.id }));
        showToast(`"${data.full_name}" created in People Master and linked.`, 'success');
      } catch (e) {
        showToast('Server error while creating person', 'error');
      }
    };

    const handleSaveShareholder = async (e) => {
      e.preventDefault();
      setSavingShareholder(true);
      try {
        const url = editingShareholderId
          ? `${BASE}/api/mca/companies/${selectedCompany.id}/shareholders/${editingShareholderId}`
          : `${BASE}/api/mca/companies/${selectedCompany.id}/shareholders`;
        // For an LLP the entry is a contribution-ledger row: no shares, the
        // "amount" is the contribution brought in and the "date" is when.
        const payload = isLLPView
          ? { ...shForm, no_of_shares: null, share_class: null, mode_of_acquisition: 'Contribution' }
          : shForm;
        const res = await fetch(url, {
          method: editingShareholderId ? 'PUT' : 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Could not save entry', 'error'); return; }
        await fetchShareholders();
        setShForm(EMPTY_SH);
        setEditingShareholderId(null);
        setShowShareholderForm(false);
        showToast(editingShareholderId ? 'Entry updated.' : 'Entry saved.', 'success');
      } catch (e) {
        showToast('Server error while saving shareholder', 'error');
      } finally {
        setSavingShareholder(false);
      }
    };

    const handleEditShareholder = (sh) => {
      setShForm({
        person_id: sh.person_id || '',
        member_name: sh.member_name || '',
        father_husband_name: sh.father_husband_name || '',
        address: sh.address || '',
        email: sh.email || '',
        pan: sh.pan || '',
        aadhaar: sh.aadhaar || '',
        folio_number: sh.folio_number || '',
        share_class: sh.share_class || 'Equity',
        distinctive_from: sh.distinctive_from || '',
        distinctive_to: sh.distinctive_to || '',
        no_of_shares: sh.no_of_shares || '',
        face_value: sh.face_value || '10',
        amount_paid: sh.amount_paid || '10',
        mode_of_acquisition: sh.mode_of_acquisition === 'Allotment' ? 'Fresh Allotment' : (sh.mode_of_acquisition || 'Fresh Allotment'),
        date_of_allotment: sh.date_of_allotment ? String(sh.date_of_allotment).slice(0, 10) : '',
        date_of_entry: sh.date_of_entry ? String(sh.date_of_entry).slice(0, 10) : '',
        date_of_cessation: sh.date_of_cessation ? String(sh.date_of_cessation).slice(0, 10) : '',
        cessation_reason: sh.cessation_reason || '',
        transferred_to: sh.transferred_to || '',
        is_active: sh.is_active !== false,
        notes: sh.notes || '',
        director_ref_id: '',
        contribution_form: sh.contribution_form || 'Cash',
      });
      setShSource('manual');
      setEditingShareholderId(sh.id);
      setShowShareholderForm(true);
    };

    const handleDeleteShareholder = async (sh) => {
      if (!await showConfirm(`Remove ${isLLPView ? 'contribution entry for' : 'shareholder'} "${sh.member_name}"?`, { title: 'Remove entry', confirmLabel: 'Remove' })) return;
      try {
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/shareholders/${sh.id}`,
          { method: 'DELETE', headers }
        );
        if (!res.ok) { showToast('Could not delete shareholder', 'error'); return; }
        await fetchShareholders();
        showToast('Entry removed.', 'success');
      } catch (e) {
        showToast('Server error while deleting shareholder', 'error');
      }
    };

    // LLP Capital Contribution tab — save a partner's obligation / received /
    // profit-share straight onto the partner record (mca_company_directors),
    // so there's one place to edit and no separate ledger.
    const handleSavePartnerContrib = async (d) => {
      const edit = contribEdits[d.id] || {};
      setContribSavingId(d.id);
      try {
        const body = {
          ...d,
          appointment_date: d.appointment_date ? String(d.appointment_date).slice(0, 10) : null,
          board_resolution_date: d.board_resolution_date ? String(d.board_resolution_date).slice(0, 10) : null,
          agm_resolution_date: d.agm_resolution_date ? String(d.agm_resolution_date).slice(0, 10) : null,
          date_admitted: d.date_admitted ? String(d.date_admitted).slice(0, 10) : null,
          dsc_expiry: d.dsc_expiry ? String(d.dsc_expiry).slice(0, 10) : null,
          date_of_birth: d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : null,
          contribution_obligation: edit.obligation ?? d.contribution_obligation ?? '',
          contribution_received: edit.received ?? d.contribution_received ?? '',
          profit_share_percent: edit.profit ?? d.profit_share_percent ?? '',
        };
        const res = await fetch(
          `${BASE}/api/mca/companies/${selectedCompany.id}/directors/${d.id}`,
          { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        );
        const data = await res.json();
        if (!res.ok) { showToast(data.message || 'Could not save', 'error'); return; }
        setContribEdits(e => { const n = { ...e }; delete n[d.id]; return n; });
        await fetchDirectors();
        showToast(`Contribution updated for ${d.director_name}.`, 'success');
      } catch (e) {
        showToast('Server error while saving contribution', 'error');
      } finally {
        setContribSavingId(null);
      }
    };

    // Tab set + labels come from the entity framework. An LLP (LLP Act, 2008)
    // has no shares, no Board/AGM meetings, no statutory committees, no AOC-2
    // and no contribution-transfer register — so those tabs simply don't
    // exist for it. Everything else is relabeled to LLP terms.
    const TAB_LABELS = {
      overview: 'Overview',
      directors: isLLPView ? 'Partners' : 'Directors',
      auditors: 'Auditors',
      shareholders: isLLPView ? 'Capital Contribution' : 'Shareholding',
      transfers: 'Share Transfers',
      meetings: isLLPView ? "Partners' Meetings" : 'Board Meetings',
      'general-meetings': 'General Meetings',
      committees: 'Committees',
      charges: 'Charges',
      contracts: 'Contracts (AOC-2)',
      filings: 'Filings',
    };
    const DETAIL_TABS = E.tabs.map(key => ({ key, label: TAB_LABELS[key] }));

    const sic = "w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500";

    return (
      <div className="space-y-5">
      <style>{`
        .dark [class~="bg-white"] { background-color: #1e293b !important; }
        .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
        .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
        .dark [class~="bg-gray-200"] { background-color: #475569 !important; }
        .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
        .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
        .dark [class~="bg-emerald-50"] { background-color: rgba(6,78,59,0.3) !important; }
        .dark [class~="bg-amber-50"] { background-color: rgba(120,53,15,0.3) !important; }

        .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-gray-100"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-blue-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-red-50"]:hover { background-color: rgba(127,29,29,0.4) !important; }
        .dark [class~="hover:bg-white"]:hover { background-color: #334155 !important; }

        .dark [class~="text-gray-400"] { color: #64748b !important; }
        .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
        .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-700"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#00206E]"] { color: #93c5fd !important; }
        .dark [class~="text-blue-500"] { color: #60a5fa !important; }
        .dark [class~="text-blue-600"] { color: #60a5fa !important; }
        .dark [class~="text-red-500"] { color: #f87171 !important; }
        .dark [class~="text-red-600"] { color: #f87171 !important; }
        .dark [class~="text-emerald-600"] { color: #34d399 !important; }
        .dark [class~="text-emerald-700"] { color: #34d399 !important; }
        .dark [class~="text-amber-600"] { color: #fbbf24 !important; }
        .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }
        .dark [class~="hover:text-blue-600"]:hover { color: #60a5fa !important; }

        .dark [class~="border-gray-50"] { border-color: #334155 !important; }
        .dark [class~="border-gray-100"] { border-color: #334155 !important; }
        .dark [class~="border-gray-200"] { border-color: #475569 !important; }
        .dark [class~="border-gray-300"] { border-color: #64748b !important; }
        .dark [class*="border-[#00206E]"] { border-color: #60a5fa !important; }
        .dark [class~="border-blue-100"] { border-color: rgba(96,165,250,0.3) !important; }
        .dark [class~="border-emerald-100"] { border-color: rgba(52,211,153,0.3) !important; }
        .dark [class~="border-red-100"] { border-color: rgba(248,113,113,0.3) !important; }
        .dark [class~="border-amber-100"] { border-color: rgba(251,191,36,0.3) !important; }
        .dark [class~="divide-gray-100"] > * + * { border-color: #334155 !important; }

        .cm-tabbar { scrollbar-width: none; -ms-overflow-style: none; }
        .cm-tabbar::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .cx-noscroll { scrollbar-width: none; -ms-overflow-style: none; }
        .cx-noscroll::-webkit-scrollbar { width: 0; height: 0; display: none; }

        .dark input, .dark select, .dark textarea {
          background-color: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #475569 !important;
        }
        .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
      `}</style>

        {/* ── DETAIL HEADER ── */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800">
          <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: 'linear-gradient(90deg,#0f1f45,#00206E 55%,#FF8400)' }} />
          <div className="p-5 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <button
                onClick={() => {
                  setSelectedCompany(null);
                  setDetailTab('overview');
                  setShareholders([]);
                  setShowShareholderForm(false);
                }}
                className="w-9 h-9 mt-1 rounded-xl flex items-center justify-center border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition flex-shrink-0"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-sm" style={{ background: 'linear-gradient(135deg,#0f1f45,#00206E)' }}>
                {(selectedCompany.company_name || '?').replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'CO'}
              </div>
              <div className="min-w-0">
                <h3 className="text-2xl font-bold text-[#0f1f45] dark:text-slate-100 truncate">{selectedCompany.company_name}</h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-[#00206E] dark:text-blue-300 border border-blue-100 dark:border-blue-900">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></svg>
                    {selectedCompany.company_type}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 dark:bg-slate-900 text-gray-600 dark:text-slate-300 border border-gray-100 dark:border-slate-700 font-mono">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                    {selectedCompany.cin_llpin || 'CIN / LLPIN not added'}
                  </span>
                  {selectedCompany.roc && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 dark:bg-slate-900 text-gray-600 dark:text-slate-300 border border-gray-100 dark:border-slate-700">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {selectedCompany.roc}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => { handleEdit(selectedCompany); setSelectedCompany(null); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] transition shadow-sm flex-shrink-0"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit Company
            </button>
          </div>
        </div>

        {/* ── COMPLIANCE-HEALTH STAT CARDS ── */}
        <div className="grid grid-cols-5 gap-4">
          {(() => {
            const openFilings = computeOpenFilingsCount(annualFilings, filings);
            const nextDue = computeNextDueDate(annualFilings);
            const dscExpiring = computeDscExpiring(directors);
            const dinKycPending = computeDinKycPending(directors);
            const lastAgm = computeLastAgmDate(generalMeetings);
            const lastLlpFy = computeLastLlpReturnFy(annualFilings);
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
            const ic = {
              cal: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
              alert: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
              key: <><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3"/></>,
              user: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="8" x2="23" y2="8"/></>,
              users: <><path d="M17 21v-2a4 4 0 0 0-3-3.87M9 21v-2a4 4 0 0 1 3-3.87M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/></>,
            };
            return [
              { label: 'Open Filings', value: openFilings, alert: openFilings > 0, tone: openFilings > 0 ? 'red' : 'plain', icon: ic.alert },
              { label: 'Next Due Date', value: fmtDate(nextDue), alert: !!nextDue && new Date(nextDue) < new Date(), tone: 'blue', icon: ic.cal },
              { label: 'DSC Expiring (≤30d)', value: dscExpiring, alert: dscExpiring > 0, tone: dscExpiring > 0 ? 'amber' : 'plain', icon: ic.key },
              { label: E.statCards.kycLabel, value: dinKycPending, alert: dinKycPending > 0, tone: dinKycPending > 0 ? 'red' : 'plain', icon: ic.user },
              isLLPView
                ? { label: 'Annual Return (Form 11)', value: lastLlpFy ? `FY ${lastLlpFy}` : '—', alert: false, tone: 'plain', icon: ic.users }
                : { label: 'Last AGM', value: fmtDate(lastAgm), alert: false, tone: 'plain', icon: ic.users },
            ].map(card => {
              const tones = {
                plain: 'bg-white border-gray-100',
                red:   'bg-red-50 border-red-100',
                amber: 'bg-amber-50 border-amber-100',
                blue:  'bg-blue-50 border-blue-100',
              };
              const valColor = card.alert ? 'text-red-600' : card.tone === 'blue' ? 'text-[#00206E]' : 'text-[#0f1f45]';
              const iconColor = card.tone === 'red' ? '#dc2626' : card.tone === 'amber' ? '#d97706' : card.tone === 'blue' ? '#2563eb' : '#94a3b8';
              return (
                <div key={card.label} className={`relative rounded-2xl border shadow-sm p-4 ${tones[card.tone]}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-xs font-medium text-gray-500">{card.label}</p>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">{card.icon}</svg>
                  </div>
                  <p className={`text-lg font-bold mt-1.5 ${valColor}`}>
                    {annualFilingsLoading && (card.label === 'Open Filings' || card.label === 'Next Due Date') ? '…' : card.value}
                  </p>
                </div>
              );
            });
          })()}
        </div>

        {/* ── TABS ── */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="cm-tabbar border-b border-gray-100 dark:border-slate-700 px-4 flex items-center gap-0.5 overflow-x-auto bg-gray-50">
            {DETAIL_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setDetailTab(tab.key);
                  if (tab.key === 'shareholders') fetchShareholders();
                  if (tab.key === 'directors') fetchDirectors();
                  if (tab.key === 'auditors') fetchAuditors();
                  if (tab.key === 'filings') fetchFilings();
                  setShowShareholderForm(false);
                  setShowDirectorForm(false);
                  setShowAuditorForm(false);
                }}
                className={`relative px-4 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                  detailTab === tab.key
                    ? 'text-[#0f1f45] dark:text-slate-100'
                    : 'text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300'
                }`}
              >
                {tab.label}
                {detailTab === tab.key && (
                  <span className="absolute left-2 right-2 -bottom-px h-[3px] rounded-full" style={{ background: '#FF8400' }} />
                )}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW TAB ── */}
          {detailTab === 'overview' && (
            <div className="p-7 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-6">
              <div className="space-y-1">
                <p className="text-[11px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-[0.08em] mb-3 pb-2 border-b border-gray-100 dark:border-slate-700">{isLLPView ? 'LLP Snapshot' : 'Company Snapshot'}</p>
                {(() => {
                  const inr = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : null);
                  if (isLLPView) {
                    const dpCount = directors.filter(d => d.is_active && d.is_designated_partner).length;
                    const received = directors
                      .filter(d => d.is_active)
                      .reduce((s, d) => s + (Number(d.contribution_received) || 0), 0);
                    return [
                      { label: 'Linked Client', value: selectedCompany.client_name },
                      { label: 'Incorporation Date', value: selectedCompany.incorporation_date ? new Date(selectedCompany.incorporation_date).toLocaleDateString('en-IN') : null },
                      { label: 'LLP Agreement Date', value: selectedCompany.date_of_agreement ? new Date(selectedCompany.date_of_agreement).toLocaleDateString('en-IN') : null },
                      { label: 'Total Contribution (obligation)', value: inr(selectedCompany.llp_contribution) },
                      { label: 'Contribution Received', value: received ? inr(received) : null },
                      { label: 'Partners', value: directors.filter(d => d.is_active).length },
                      { label: 'Designated Partners', value: dpCount },
                    ];
                  }
                  return [
                    { label: 'Linked Client', value: selectedCompany.client_name },
                    { label: 'Incorporation Date', value: selectedCompany.incorporation_date ? new Date(selectedCompany.incorporation_date).toLocaleDateString('en-IN') : null },
                    { label: 'Authorized Capital', value: inr(selectedCompany.authorized_capital) },
                    { label: 'Paid-up Capital', value: inr(selectedCompany.paid_up_capital) },
                    { label: 'Directors', value: directors.length },
                  ];
                })().map(row => (
                  <div key={row.label} className="flex justify-between items-start gap-3 py-2.5 border-b border-gray-50 dark:border-slate-700/60">
                    <span className="text-[13px] text-gray-500 dark:text-slate-400 flex-shrink-0">{row.label}</span>
                    <span className="text-[13px] font-semibold text-[#0f1f45] dark:text-slate-100 text-right">{row.value ?? '-'}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-[0.08em] mb-3 pb-2 border-b border-gray-100 dark:border-slate-700">Statutory Identifiers</p>
                {[
                  { label: isLLPView ? 'LLPIN' : 'CIN', value: selectedCompany.cin_llpin },
                  { label: 'PAN', value: selectedCompany.pan },
                  { label: 'TAN', value: selectedCompany.tan },
                  { label: (selectedCompany.gst_registrations || []).length ? 'GSTIN (Principal)' : 'GSTIN', value: selectedCompany.gstin },
                  ...((selectedCompany.gst_registrations || []).map((g, i) => ({
                    label: `GSTIN — ${g.state || `State ${i + 1}`}`, value: g.gstin,
                  }))),
                  { label: 'ESIC No.', value: selectedCompany.esic },
                  { label: 'EPFO No.', value: selectedCompany.epfo },
                  { label: 'ROC', value: selectedCompany.roc },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-start gap-3 py-2.5 border-b border-gray-50 dark:border-slate-700/60">
                    <span className="text-[13px] text-gray-500 dark:text-slate-400 flex-shrink-0">{row.label}</span>
                    <span className="text-[13px] font-semibold text-[#0f1f45] dark:text-slate-100 text-right font-mono">{row.value || '-'}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <p className="text-[11px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-[0.08em] mb-3 pb-2 border-b border-gray-100 dark:border-slate-700">Contact</p>
                {[
                  { label: 'Email', value: selectedCompany.email },
                  { label: 'Mobile', value: selectedCompany.mobile },
                  { label: 'Registered Office', value: selectedCompany.registered_office },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-start gap-3 py-2.5 border-b border-gray-50 dark:border-slate-700/60">
                    <span className="text-[13px] text-gray-500 dark:text-slate-400 flex-shrink-0">{row.label}</span>
                    <span className="text-[13px] font-semibold text-[#0f1f45] dark:text-slate-100 text-right max-w-[62%]">{row.value || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── DIRECTORS TAB ── */}
          {detailTab === 'directors' && (
            <div>

              {/* Toolbar */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">
                    {isLLPView ? 'Partners & Designated Partners' : 'Board of Directors'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                    {directors.length} {directors.length === 1 ? E.party.one.toLowerCase() : E.party.many.toLowerCase()} · {isLLPView ? 'admission and cessation records' : 'appointment and cessation records'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSyncPeople}
                    disabled={syncingPeople}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-60 transition"
                  >
                    {syncingPeople ? 'Syncing…' : 'Sync to People Master'}
                  </button>
                  <button
                    onClick={() => {
                      setDirForm({ ...EMPTY_DIR, designation: isLLPView ? 'Partner' : 'Director' });
                      setOdDraft(EMPTY_OD);
                      setOdOpen(false);
                      setEditingDirectorId(null);
                      setShowDirectorForm(true);
                      setShowImportSuggestion(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] transition shadow-sm"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    {E.party.addLabel}
                  </button>
                </div>
              </div>

              {/* ERP Import Suggestion Banner */}
              {showImportSuggestion && importablePeople.length > 0 && (
                <div className="mx-6 mt-5 rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,32,110,0.15)' }}>
                  <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #eff6ff, #fff7ed)' }}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: '#00206E' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">
                          {importablePeople.length} {importablePeople.length === 1 ? E.party.one : E.party.many} found in ERP People Module
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                          The following {importablePeople.length === 1 ? 'person is' : 'people are'} linked to this client as {isLLPView ? 'partners' : 'directors'} in your ERP:
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {importablePeople.map(p => (
                            <span key={p.id} className="px-2.5 py-1 bg-white dark:bg-slate-800 rounded-lg text-xs font-semibold text-[#0f1f45] dark:text-slate-100 border border-blue-100 shadow-sm">
                              {p.full_name}
                              {p.din && <span className="text-gray-400 dark:text-slate-500 font-normal ml-1">· DIN {p.din}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button
                        onClick={() => setShowImportSuggestion(false)}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 text-xs font-medium text-gray-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 transition"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={handleImportFromPeople}
                        disabled={importing}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-xs font-semibold bg-[#0f1f45] hover:bg-[#162d5e] disabled:opacity-60 transition shadow-sm"
                      >
                        {importing ? (
                          'Importing...'
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            Import All
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Director Form */}
              {showDirectorForm && (
                <div
                  className="fixed inset-0 z-[110] flex justify-center items-start overflow-y-auto py-6 px-4 dir-modal-scope"
                  style={{ background: 'rgba(15,23,42,0.6)' }}
                  onMouseDown={() => { setShowDirectorForm(false); setEditingDirectorId(null); setDirForm(EMPTY_DIR); }}
                >
                  <style>{`
                    .dir-modal-scope ::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
                    .dir-modal-scope * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                  `}</style>
                  <form
                    onSubmit={handleSaveDirector}
                    onMouseDown={e => e.stopPropagation()}
                    className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl my-auto flex flex-col"
                    style={{ maxHeight: 'calc(100vh - 3rem)' }}
                  >
                    {/* Header */}
                    <div className="px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #0f1f45 0%, #00206E 55%, #1d4ed8 100%)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        </div>
                        <div>
                          <p className="text-base font-bold text-white">{editingDirectorId ? `Edit ${E.party.one}` : E.party.addLabel}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(191,219,254,0.85)' }}>
                            {isLLPView
                              ? 'Full KYC — flows into the Contribution Register and the ERP People module on sync.'
                              : 'Full KYC — this data auto-fills the Register of Members and the ERP People module on sync.'}
                          </p>
                        </div>
                      </div>
                      <button type="button"
                        onClick={() => { setShowDirectorForm(false); setEditingDirectorId(null); setDirForm(EMPTY_DIR); }}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/15 transition">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>

                    {/* Body (scrollbar hidden) */}
                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">

                      {/* A — Role & People link */}
                      <DirSection title="Role & People Master Link" hint={isLLPView ? E.party.residentNote : 'Link an existing person to reuse their KYC, or fill fresh and create the People record from here.'}>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Link to People Master</label>
                            <select value={dirForm.person_id} onChange={e => handleDirPersonSelect(e.target.value)} className={sic}>
                              <option value="">Not linked — new person</option>
                              {persons.map(p => (
                                <option key={p.id} value={p.id}>{p.full_name}{p.pan_number ? ` · ${p.pan_number}` : ''}</option>
                              ))}
                            </select>
                            {!dirForm.person_id && dirForm.director_name && (
                              <button type="button" onClick={handleCreatePersonFromDirector}
                                className="mt-1.5 text-[11px] font-semibold text-[#00206E] dark:text-blue-400 hover:underline">
                                ＋ Create &amp; link in People Master
                              </button>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Designation</label>
                            {isLLPView ? (
                              <select value={dirForm.designation}
                                onChange={e => setDirForm(f => ({ ...f, designation: e.target.value, is_designated_partner: e.target.value === 'Designated Partner' }))}
                                className={sic}>
                                <option>Partner</option>
                                <option>Designated Partner</option>
                              </select>
                            ) : (
                              <select value={dirForm.designation} onChange={e => setDirForm(f => ({ ...f, designation: e.target.value }))} className={sic}>
                                <option>Director</option>
                                <option>Managing Director</option>
                                <option>Whole Time Director</option>
                                <option>Independent Director</option>
                                <option>Nominee Director</option>
                                <option>Additional Director</option>
                                <option>Alternate Director</option>
                              </select>
                            )}
                          </div>
                          <div className="flex items-end pb-2.5">
                            {isLLPView ? (
                              <p className="text-[11px] text-gray-400 leading-tight">
                                Set <b>Designation → Designated Partner</b> to mark a DP.
                                An LLP needs at least 2 DPs, one resident in India.
                              </p>
                            ) : (
                              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-slate-300">
                                <input type="checkbox" checked={dirForm.is_independent_director}
                                  onChange={e => setDirForm(f => ({ ...f, is_independent_director: e.target.checked }))} />
                                Independent (Sec 149(6))
                              </label>
                            )}
                          </div>
                        </div>
                      </DirSection>

                      {/* B — Personal / statutory identity */}
                      <DirSection title="Personal & Statutory Identity" accent="#7c3aed">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Name in Full *</label>
                            <input required value={dirForm.director_name}
                              onChange={e => setDirForm(f => ({ ...f, director_name: e.target.value }))}
                              placeholder="Full legal name as per PAN" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{isLLPView ? 'DPIN / DIN' : 'DIN / DPIN'}</label>
                            <input value={dirForm.din} onChange={e => setDirForm(f => ({ ...f, din: e.target.value }))} placeholder="8-digit number" className={sic} />
                          </div>
                          {isLLPView && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Partner Type</label>
                              <select value={dirForm.partner_type} onChange={e => setDirForm(f => ({ ...f, partner_type: e.target.value }))} className={sic}>
                                {E.partnerTypes.map(pt => <option key={pt}>{pt}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">PAN</label>
                            <input value={dirForm.pan} onChange={e => setDirForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} maxLength={10} placeholder="ABCDE1234F" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Aadhaar No.</label>
                            <input value={dirForm.aadhaar} onChange={e => setDirForm(f => ({ ...f, aadhaar: e.target.value }))} placeholder="12-digit" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Birth</label>
                            <input type="date" value={dirForm.date_of_birth} onChange={e => setDirForm(f => ({ ...f, date_of_birth: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Father's Name</label>
                            <input value={dirForm.father_name} onChange={e => setDirForm(f => ({ ...f, father_name: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Place of Birth</label>
                            <input value={dirForm.place_of_birth} onChange={e => setDirForm(f => ({ ...f, place_of_birth: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Nationality</label>
                            <input value={dirForm.nationality} onChange={e => setDirForm(f => ({ ...f, nationality: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Occupation</label>
                            <input value={dirForm.occupation} onChange={e => setDirForm(f => ({ ...f, occupation: e.target.value }))} placeholder="e.g. Business, Service" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Educational Qualification</label>
                            <input value={dirForm.education_qualification} onChange={e => setDirForm(f => ({ ...f, education_qualification: e.target.value }))} placeholder="e.g. B.Com, CA" className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      {/* C — Contact & address */}
                      <DirSection title="Contact & Address" accent="#0d9488">
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Email ID</label>
                            <input type="email" value={dirForm.email} onChange={e => setDirForm(f => ({ ...f, email: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Mobile No.</label>
                            <input value={dirForm.mobile} onChange={e => setDirForm(f => ({ ...f, mobile: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Secondary Phone No.</label>
                            <input value={dirForm.secondary_mobile} onChange={e => setDirForm(f => ({ ...f, secondary_mobile: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">DSC Expiry</label>
                            <input type="date" value={dirForm.dsc_expiry} onChange={e => setDirForm(f => ({ ...f, dsc_expiry: e.target.value }))} className={sic} />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Address (as per Aadhaar)</label>
                            <textarea rows={2} value={dirForm.address_aadhaar}
                              onChange={e => setDirForm(f => ({ ...f, address_aadhaar: e.target.value }))}
                              placeholder="Full residential address" className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      {/* C2 — Contribution & profit share (LLP only) */}
                      {isLLPView && (
                        <DirSection title="Contribution & Profit Share" accent="#0891b2"
                          hint="As agreed in the LLP Agreement — disclosed in Form 11. Contribution may be brought in instalments (track them in the Capital Contribution tab).">
                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Obligation to Contribute (₹)</label>
                              <input type="number" value={dirForm.contribution_obligation}
                                onChange={e => setDirForm(f => ({ ...f, contribution_obligation: e.target.value }))} className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Contribution Received (₹)</label>
                              <input type="number" value={dirForm.contribution_received}
                                onChange={e => setDirForm(f => ({ ...f, contribution_received: e.target.value }))} className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Profit-Sharing Ratio (%)</label>
                              <input type="number" value={dirForm.profit_share_percent}
                                onChange={e => setDirForm(f => ({ ...f, profit_share_percent: e.target.value }))} placeholder="e.g. 50" className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date Admitted</label>
                              <input type="date" value={dirForm.date_admitted}
                                onChange={e => setDirForm(f => ({ ...f, date_admitted: e.target.value }))} className={sic} />
                            </div>
                          </div>
                        </DirSection>
                      )}

                      {/* D — Other directorships / interests (Sec 184-equivalent) */}
                      <DirSection title={isLLPView ? 'Other Directorships / LLP Interests' : 'Other Directorships / Interests'} accent="#d97706"
                        hint={isLLPView
                          ? 'Companies / LLPs where this person is already a director or partner — disclosure of interest for related-party purposes.'
                          : 'Companies / LLPs where this person is already a director or holds shares — declaration u/s 184.'}>
                        <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-slate-300 mb-3">
                          <input type="checkbox" checked={odOpen}
                            onChange={e => setOdOpen(e.target.checked)} />
                          This person is a director / partner in other entities
                        </label>
                        {odOpen && (
                          <div className="rounded-xl border border-gray-200 dark:border-slate-600 p-4 bg-gray-50">
                            <div className="grid grid-cols-5 gap-3">
                              <input value={odDraft.cin} onChange={e => setOdDraft(d => ({ ...d, cin: e.target.value.toUpperCase() }))} placeholder="CIN / LLPIN" className={sic} />
                              <input value={odDraft.company_name} onChange={e => setOdDraft(d => ({ ...d, company_name: e.target.value }))} placeholder="Name of Company" className={sic} />
                              <input value={odDraft.designation} onChange={e => setOdDraft(d => ({ ...d, designation: e.target.value }))} placeholder="Designation" className={sic} />
                              <input type="number" value={odDraft.shareholding_percent} onChange={e => setOdDraft(d => ({ ...d, shareholding_percent: e.target.value }))} placeholder="% Holding" className={sic} />
                              <input type="number" value={odDraft.shareholding_amount} onChange={e => setOdDraft(d => ({ ...d, shareholding_amount: e.target.value }))} placeholder="Amount ₹" className={sic} />
                            </div>
                            <button type="button" onClick={addOtherDirectorship}
                              className="mt-3 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-xs font-semibold text-[#00206E] dark:text-blue-400 hover:bg-white dark:hover:bg-slate-700 transition">
                              ＋ Add Entity
                            </button>
                            {(dirForm.other_directorships || []).length > 0 && (
                              <div className="mt-3 space-y-1.5">
                                {(dirForm.other_directorships || []).map(ent => (
                                  <div key={ent.id} className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg px-3 py-2 text-xs border border-gray-100 dark:border-slate-700">
                                    <div className="min-w-0">
                                      <span className="font-semibold text-gray-700 dark:text-slate-200">{ent.company_name || ent.cin || '—'}</span>
                                      <span className="text-gray-400 dark:text-slate-500">
                                        {ent.cin ? ` · ${ent.cin}` : ''}{ent.designation ? ` · ${ent.designation}` : ''}
                                        {ent.shareholding_percent ? ` · ${ent.shareholding_percent}%` : ''}
                                        {ent.shareholding_amount ? ` · ₹${Number(ent.shareholding_amount).toLocaleString('en-IN')}` : ''}
                                      </span>
                                    </div>
                                    <button type="button" onClick={() => removeOtherDirectorship(ent.id)} className="text-red-500 hover:underline flex-shrink-0 ml-3">Remove</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </DirSection>

                    {/* Section 2 — Appointment / Admission */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-3">{isLLPView ? 'Admission Details' : 'Appointment Details'}</p>
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-slate-300 mb-3">
                        <input type="checkbox" checked={dirForm.appointed_at_incorporation}
                          onChange={e => {
                            const checked = e.target.checked;
                            const incDate = selectedCompany.incorporation_date ? String(selectedCompany.incorporation_date).slice(0, 10) : '';
                            setDirForm(f => checked
                              ? { ...f, appointed_at_incorporation: true,
                                  appointment_date: incDate || f.appointment_date,
                                  date_admitted: incDate || f.date_admitted,
                                  board_resolution_date: '', agm_resolution_date: '',
                                  form_dir12_filed: false, form_dir12_srn: '', form_dir12_date: '',
                                  form4_appointment_srn: '', form4_appointment_date: '' }
                              : { ...f, appointed_at_incorporation: false });
                          }} />
                        {isLLPView ? 'First Partner — named in Incorporation (FiLLiP)' : 'First Director — Appointed at Incorporation (Sec 152(1))'}
                      </label>
                      {dirForm.appointed_at_incorporation && (
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3 -mt-2">
                          {isLLPView
                            ? 'Named in the incorporation filing (FiLLiP) — admission is effective from the LLP incorporation date; no Form 4 applies.'
                            : "Appointed on the company's incorporation date via the incorporation filing (SPICe+/INC-32) — no Board/AGM resolution and no DIR-12 apply."}
                        </p>
                      )}
                      {(() => {
                        const isBoardRoute = ['Additional Director', 'Alternate Director', 'Nominee Director'].includes(dirForm.designation);
                        const incDateFmt = selectedCompany.incorporation_date ? new Date(selectedCompany.incorporation_date).toLocaleDateString('en-IN') : '—';
                        if (isLLPView) {
                          return (
                            <div className="grid grid-cols-4 gap-4">
                              {dirForm.appointed_at_incorporation ? (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Admission</label>
                                  <input readOnly value={incDateFmt} className={`${sic} bg-gray-50 cursor-not-allowed`} />
                                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">= LLP Incorporation Date</p>
                                </div>
                              ) : (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Admission</label>
                                  <input type="date" value={dirForm.date_admitted || dirForm.appointment_date}
                                    onChange={e => setDirForm(f => ({ ...f, date_admitted: e.target.value, appointment_date: e.target.value }))}
                                    className={sic} />
                                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Date named in the supplementary LLP Agreement / Form 4.</p>
                                </div>
                              )}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Status</label>
                                <select value={dirForm.is_active ? 'active' : 'inactive'}
                                  onChange={e => setDirForm(f => ({ ...f, is_active: e.target.value === 'active' }))}
                                  className={sic}>
                                  <option value="active">Active</option>
                                  <option value="inactive">Ceased</option>
                                </select>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="grid grid-cols-4 gap-4">
                            {dirForm.appointed_at_incorporation ? (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointment Date</label>
                                <input readOnly value={incDateFmt} className={`${sic} bg-gray-50 cursor-not-allowed`} />
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">= Date of Incorporation</p>
                              </div>
                            ) : (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointment Date</label>
                                <input
                                  type="date"
                                  value={dirForm.appointment_date}
                                  onChange={e => setDirForm(f => ({ ...f, appointment_date: e.target.value }))}
                                  className={sic}
                                />
                              </div>
                            )}
                            {!dirForm.appointed_at_incorporation && (isBoardRoute ? (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Board Resolution Date</label>
                                  <input
                                    type="date"
                                    value={dirForm.board_resolution_date}
                                    onChange={e => setDirForm(f => ({ ...f, board_resolution_date: e.target.value }))}
                                    className={sic}
                                  />
                                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Sec 161 — date the Board appointed this director.</p>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Regularized at AGM</label>
                                  <input
                                    type="date"
                                    value={dirForm.agm_resolution_date}
                                    onChange={e => setDirForm(f => ({ ...f, agm_resolution_date: e.target.value }))}
                                    className={sic}
                                  />
                                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">Sec 161(1) — an {dirForm.designation} automatically vacates office if not confirmed by ordinary resolution at the next AGM. Leave blank until regularized.</p>
                                </div>
                              </>
                            ) : (
                              <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointment Resolution Date (AGM/EGM)</label>
                                <input
                                  type="date"
                                  value={dirForm.agm_resolution_date}
                                  onChange={e => setDirForm(f => ({ ...f, agm_resolution_date: e.target.value }))}
                                  className={sic}
                                />
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Sec 152(2)/(6) — appointed/re-appointed directly by members' ordinary resolution; no Board Resolution applies to this route.</p>
                              </div>
                            ))}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Status</label>
                              <select
                                value={dirForm.is_active ? 'active' : 'inactive'}
                                onChange={e => setDirForm(f => ({ ...f, is_active: e.target.value === 'active' }))}
                                className={sic}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Ceased</option>
                              </select>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Section 3 — DIR-12 Filing (not required for first directors — appointed via the incorporation filing itself; applies to appointments after incorporation only) */}
                    {!dirForm.appointed_at_incorporation && isLLPView && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-1">Form 4 Filing (Appointment / Change)</p>
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3">Notice to the ROC of appointment / change of a partner (LLP Act s.25, Rule 22) — file within 30 days along with Form 3 (supplementary LLP Agreement).</p>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 4 Filed</label>
                            <select value={dirForm.form4_appointment_srn ? 'yes' : 'no'}
                              onChange={e => setDirForm(f => ({ ...f, form4_appointment_srn: e.target.value === 'yes' ? f.form4_appointment_srn : '' }))}
                              className={sic}>
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 4 SRN</label>
                            <input value={dirForm.form4_appointment_srn}
                              onChange={e => setDirForm(f => ({ ...f, form4_appointment_srn: e.target.value.toUpperCase() }))}
                              placeholder="SRN Number" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Filing Date</label>
                            <input type="date" value={dirForm.form4_appointment_date}
                              onChange={e => setDirForm(f => ({ ...f, form4_appointment_date: e.target.value }))}
                              className={sic} />
                          </div>
                        </div>
                      </div>
                    )}

                    {!dirForm.appointed_at_incorporation && !isLLPView && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-1">DIR-12 Filing (Appointment)</p>
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3">A statutory intimation to the ROC (Sec 170(2)/Rule 18) — filing this does not itself create or validate the appointment, which is effective from the resolution date above.</p>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">DIR-12 Filed</label>
                            <select
                              value={dirForm.form_dir12_filed ? 'yes' : 'no'}
                              onChange={e => setDirForm(f => ({ ...f, form_dir12_filed: e.target.value === 'yes' }))}
                              className={sic}
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </div>
                          {dirForm.form_dir12_filed && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">SRN</label>
                                <input
                                  value={dirForm.form_dir12_srn}
                                  onChange={e => setDirForm(f => ({ ...f, form_dir12_srn: e.target.value.toUpperCase() }))}
                                  placeholder="SRN Number"
                                  className={sic}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Filing Date</label>
                                <input
                                  type="date"
                                  value={dirForm.form_dir12_date}
                                  onChange={e => setDirForm(f => ({ ...f, form_dir12_date: e.target.value }))}
                                  className={sic}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Section 3b — DIN/DPIN KYC (a personal obligation, always shown) */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-3">{isLLPView ? 'DPIN KYC (DIR-3 KYC — Rule 12A, once every 3 FYs)' : 'DIN KYC (Rule 12A — triennial)'}</p>
                      <div className="grid grid-cols-4 gap-4">
                        {(() => { const dinInactive = dirForm.din_status === 'Deactivated'; return (<>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{isLLPView ? 'DPIN Status' : 'DIN Status'}</label>
                          <select
                            value={dirForm.din_status}
                            onChange={e => {
                              const v = e.target.value;
                              setDirForm(f => ({
                                ...f,
                                din_status: v,
                                // A deactivated DIN/DPIN can't file KYC — force Not Applicable.
                                din_kyc_status: v === 'Deactivated' ? 'Not Applicable' : (f.din_kyc_status === 'Not Applicable' ? 'Pending' : f.din_kyc_status),
                              }));
                            }}
                            className={sic}
                          >
                            <option>Active</option>
                            <option>Deactivated</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{isLLPView ? 'DPIN KYC Status' : 'DIN KYC Status'}</label>
                          <select
                            value={dirForm.din_kyc_status}
                            onChange={e => setDirForm(f => ({ ...f, din_kyc_status: e.target.value }))}
                            disabled={dinInactive}
                            className={`${sic} ${dinInactive ? 'bg-gray-50 dark:bg-slate-900 cursor-not-allowed opacity-70' : ''}`}
                          >
                            <option>Pending</option>
                            <option>Done</option>
                            <option>Not Applicable</option>
                          </select>
                          {dinInactive && <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">{isLLPView ? 'DPIN' : 'DIN'} deactivated — KYC not applicable.</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Last DIR-3 KYC Filed FY</label>
                          <input
                            value={dirForm.din_kyc_year}
                            onChange={e => setDirForm(f => ({ ...f, din_kyc_year: e.target.value }))}
                            placeholder="e.g. 2025-26"
                            disabled={dinInactive}
                            className={`${sic} ${dinInactive ? 'bg-gray-50 dark:bg-slate-900 cursor-not-allowed opacity-70' : ''}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Next Due FY (triennial)</label>
                          <input
                            value={dinInactive ? '—' : (computeNextDueFy(dirForm.din_kyc_year) || '—')}
                            readOnly
                            className={`${sic} bg-gray-50 dark:bg-slate-900 cursor-not-allowed`}
                          />
                        </div>
                        </>); })()}
                        {dirForm.din_status !== 'Deactivated' && isKycDueSoon(dirForm.din_kyc_year) && (
                          <div className="col-span-4 -mt-1">
                            <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">⚠ DIR-3 KYC due — file DIR-3 KYC (Web) by 30 September to avoid the ₹5,000 late fee and {isLLPView ? 'DPIN' : 'DIN'} deactivation (Rule 12A). Once every 3 financial years since the 2026 Amendment Rules.</p>
                          </div>
                        )}
                        {!isKycDueSoon(dirForm.din_kyc_year) && (
                          <div className="col-span-4 -mt-1">
                            <p className="text-[11px] text-gray-400 dark:text-slate-500">DIR-3 KYC is filed once every 3 financial years (2026 Amendment Rules), by 30 September. {dirForm.din_kyc_year ? `Last filed FY ${dirForm.din_kyc_year}.` : ''}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section 4 — Cessation (conditional) */}
                    {!dirForm.is_active && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-3">Cessation Details</p>
                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Cessation Date</label>
                            <input
                              type="date"
                              value={dirForm.cessation_date}
                              onChange={e => setDirForm(f => ({ ...f, cessation_date: e.target.value }))}
                              className={sic}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Reason</label>
                            <select
                              value={dirForm.cessation_reason}
                              onChange={e => setDirForm(f => ({ ...f, cessation_reason: e.target.value }))}
                              className={sic}
                            >
                              <option value="">Select reason</option>
                              {E.cessationReasons.map(r => <option key={r}>{r}</option>)}
                            </select>
                          </div>
                          {isLLPView ? (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 4 Cessation SRN</label>
                                <input value={dirForm.form4_cessation_srn}
                                  onChange={e => setDirForm(f => ({ ...f, form4_cessation_srn: e.target.value.toUpperCase() }))}
                                  className={sic} />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 4 Cessation Date</label>
                                <input type="date" value={dirForm.form4_cessation_date}
                                  onChange={e => setDirForm(f => ({ ...f, form4_cessation_date: e.target.value }))}
                                  className={sic} />
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Resolution Date</label>
                                <input
                                  type="date"
                                  value={dirForm.cessation_resolution_date}
                                  onChange={e => setDirForm(f => ({ ...f, cessation_resolution_date: e.target.value }))}
                                  className={sic}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">DIR-12 Cessation Filed</label>
                                <select
                                  value={dirForm.form_dir12_cessation_filed ? 'yes' : 'no'}
                                  onChange={e => setDirForm(f => ({ ...f, form_dir12_cessation_filed: e.target.value === 'yes' }))}
                                  className={sic}
                                >
                                  <option value="no">No</option>
                                  <option value="yes">Yes</option>
                                </select>
                              </div>
                              {dirForm.form_dir12_cessation_filed && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Cessation SRN</label>
                                  <input
                                    value={dirForm.form_dir12_cessation_srn}
                                    onChange={e => setDirForm(f => ({ ...f, form_dir12_cessation_srn: e.target.value.toUpperCase() }))}
                                    className={sic}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                      {/* Notes */}
                      <DirSection title="Notes" accent="#64748b">
                        <input
                          value={dirForm.notes}
                          onChange={e => setDirForm(f => ({ ...f, notes: e.target.value }))}
                          placeholder="Internal notes"
                          className={sic}
                        />
                      </DirSection>

                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-3 flex-shrink-0 rounded-b-2xl">
                      <button
                        type="button"
                        onClick={() => { setShowDirectorForm(false); setEditingDirectorId(null); setDirForm(EMPTY_DIR); }}
                        className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 text-sm font-medium hover:bg-white dark:hover:bg-slate-700 transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={savingDirector}
                        className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 transition shadow-sm" style={{ background: '#00206E' }}
                      >
                        {savingDirector ? 'Saving...' : `${editingDirectorId ? 'Update' : 'Save'} ${E.party.one}`}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Directors List */}
              {directorsLoading ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">Loading directors...</div>
              ) : directors.length === 0 && !showDirectorForm ? (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-500 dark:text-slate-400">{E.party.emptyText}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Add {E.party.many.toLowerCase()} manually or import from ERP People module</p>
                </div>
              ) : directors.length > 0 && (
                <div className="p-6 grid grid-cols-1 gap-3">
                  {directors.map(d => (
                    <div
                      key={d.id}
                      className={`relative rounded-2xl border overflow-hidden transition-all ${
                        d.is_active
                          ? 'border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-100 dark:hover:border-blue-900 hover:shadow-sm'
                          : 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 opacity-70'
                      }`}
                    >
                      <div className="px-5 py-4 flex items-center justify-between gap-4">

                        {/* Left — Avatar + Name */}
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                            d.is_active
                              ? 'text-white'
                              : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                          }`}
                          style={d.is_active ? { background: '#00206E' } : {}}>
                            {d.director_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100 truncate">{d.director_name}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${
                                d.is_active
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900'
                                  : 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900'
                              }`}>
                                {d.is_active ? 'Active' : 'Ceased'}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 border border-blue-100 flex-shrink-0">
                                {d.designation}
                              </span>
                              {isLLPView && d.is_designated_partner && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 border border-violet-100 dark:border-violet-900 flex-shrink-0">
                                  DP
                                </span>
                              )}
                              {!isLLPView && d.designation === 'Additional Director' && d.is_active && !d.agm_resolution_date && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-900 flex-shrink-0">
                                  Regularization pending
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                              {d.din ? `${isLLPView ? 'DPIN' : 'DIN'}: ${d.din}` : `${isLLPView ? 'DPIN' : 'DIN'} not added`}
                              {d.pan ? ` · PAN: ${d.pan}` : ''}
                              {d.email ? ` · ${d.email}` : ''}
                            </p>
                          </div>
                        </div>

                        {/* Right — Details + Actions */}
                        <div className="flex items-center gap-6 flex-shrink-0">
                          {isLLPView ? (
                            <div className="text-right hidden md:block">
                              <p className="text-xs text-gray-400 dark:text-slate-500">Profit Share</p>
                              <p className="text-xs font-semibold text-gray-700 dark:text-slate-300">
                                {d.profit_share_percent != null && d.profit_share_percent !== '' ? `${d.profit_share_percent}%` : '-'}
                              </p>
                            </div>
                          ) : null}
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">{isLLPView ? 'Admitted' : 'Appointed'}</p>
                            <p className="text-xs font-semibold text-gray-700 dark:text-slate-300">
                              {(() => { const dt = isLLPView ? (d.date_admitted || d.appointment_date) : d.appointment_date; return dt ? new Date(dt).toLocaleDateString('en-IN') : '-'; })()}
                            </p>
                          </div>
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">DSC Expiry</p>
                            <p className={`text-xs font-semibold ${
                              d.dsc_expiry && new Date(d.dsc_expiry) < new Date()
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-700 dark:text-slate-300'
                            }`}>
                              {d.dsc_expiry ? new Date(d.dsc_expiry).toLocaleDateString('en-IN') : '-'}
                            </p>
                          </div>
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">{isLLPView ? 'Form 4' : 'DIR-12'}</p>
                            {isLLPView ? (
                              <span className={`text-xs font-semibold ${
                                d.appointed_at_incorporation ? 'text-gray-400 dark:text-slate-500'
                                  : d.form4_appointment_srn ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                              }`}>
                                {d.appointed_at_incorporation ? 'N/A (FiLLiP)' : d.form4_appointment_srn ? '✓ Filed' : 'Pending'}
                              </span>
                            ) : (
                              <span className={`text-xs font-semibold ${
                                d.appointed_at_incorporation ? 'text-gray-400 dark:text-slate-500'
                                  : d.form_dir12_filed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                              }`}>
                                {d.appointed_at_incorporation ? 'N/A' : d.form_dir12_filed ? '✓ Filed' : 'Pending'}
                              </span>
                            )}
                          </div>
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">{isLLPView ? 'DPIN KYC' : 'DIN KYC'}</p>
                            <span className={`text-xs font-semibold ${
                              d.din_kyc_status === 'Done' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                            }`}>
                              {d.din_kyc_status || 'Pending'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleEditDirector(d)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 transition"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteDirector(d)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-500 transition"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── AUDITORS TAB (Statutory Auditor Master — mca_company_auditors) ── */}
          {detailTab === 'auditors' && (
            <div>
              {/* Toolbar */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">Statutory Auditors</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                    {auditors.length} record{auditors.length === 1 ? '' : 's'} · auto-filled by Passed First Auditor / AGM Appointment / Casual Vacancy resolutions, or add manually below
                  </p>
                </div>
                <button
                  onClick={() => { setAuditorForm(EMPTY_AUDITOR); setJointAuditorDraft(EMPTY_JOINT_AUDITOR); setPriorTenureDraft(EMPTY_PRIOR_TENURE_ROW); setEditingAuditorId(null); setShowAuditorForm(true); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] transition shadow-sm"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Auditor
                </button>
              </div>

              {/* Add/Edit Modal */}
              {showAuditorForm && (
                <div
                  className="fixed inset-0 z-[110] flex justify-center items-start overflow-y-auto py-6 px-4"
                  style={{ background: 'rgba(15,23,42,0.6)' }}
                  onMouseDown={() => { setShowAuditorForm(false); setEditingAuditorId(null); setAuditorForm(EMPTY_AUDITOR); }}
                >
                  <form
                    onSubmit={handleSaveAuditor}
                    onMouseDown={e => e.stopPropagation()}
                    className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl my-auto flex flex-col"
                    style={{ maxHeight: 'calc(100vh - 3rem)' }}
                  >
                    <div className="px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #0f1f45 0%, #00206E 55%, #1d4ed8 100%)' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        </div>
                        <div>
                          <p className="text-base font-bold text-white">{editingAuditorId ? 'Edit Auditor' : 'Add Auditor'}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'rgba(191,219,254,0.85)' }}>Feeds the Board Report's "Statutory Auditors" clause — appointment category and confirmation status drive its wording (Sec 139(1)/(6)/(8)).</p>
                        </div>
                      </div>
                      <button type="button"
                        onClick={() => { setShowAuditorForm(false); setEditingAuditorId(null); setAuditorForm(EMPTY_AUDITOR); }}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/15 transition">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
                      <DirSection title="Firm Details">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Firm Name *</label>
                            <input required value={auditorForm.firm_name} onChange={e => setAuditorForm(f => ({ ...f, firm_name: e.target.value }))} placeholder="M/s ABC & Co., Chartered Accountants" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Firm Registration No. (FRN)</label>
                            <input value={auditorForm.frn} onChange={e => setAuditorForm(f => ({ ...f, frn: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Peer Review No.</label>
                            <input value={auditorForm.peer_review_no} onChange={e => setAuditorForm(f => ({ ...f, peer_review_no: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Audit Fee (₹)</label>
                            <input type="number" value={auditorForm.audit_fee} onChange={e => setAuditorForm(f => ({ ...f, audit_fee: e.target.value }))} className={sic} />
                          </div>
                          <div className="flex items-end pb-2.5">
                            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                              <input type="checkbox" checked={auditorForm.is_active} onChange={e => setAuditorForm(f => ({ ...f, is_active: e.target.checked }))} />
                              Currently the active Statutory Auditor
                            </label>
                          </div>
                        </div>
                      </DirSection>

                      <DirSection title="Appointment" accent="#d97706" hint="Category + confirmation status together decide which Board Report wording (First Auditor / regular / casual vacancy, pending vs confirmed) gets auto-selected.">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointment Category</label>
                            <select value={auditorForm.appointment_category} onChange={e => setAuditorForm(f => ({ ...f, appointment_category: e.target.value }))} className={sic}>
                              <option>First Auditor</option>
                              <option>Regular Appointment</option>
                              <option>Re-appointment</option>
                              <option>Casual Vacancy</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointed By</label>
                            <select value={auditorForm.appointed_by} onChange={e => setAuditorForm(f => ({ ...f, appointed_by: e.target.value }))} className={sic}>
                              <option value="Board">Board</option>
                              <option value="AGM">AGM</option>
                              <option value="EGM">EGM</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Confirmation Status</label>
                            <select value={auditorForm.confirmation_status} onChange={e => setAuditorForm(f => ({ ...f, confirmation_status: e.target.value }))} className={sic}>
                              <option value="Confirmed">Confirmed</option>
                              <option value="Pending EGM Confirmation">Pending EGM Confirmation</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Board Resolution Date</label>
                            <input type="date" value={auditorForm.board_resolution_date} onChange={e => setAuditorForm(f => ({ ...f, board_resolution_date: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">AGM / EGM Resolution Date</label>
                            <input type="date" value={auditorForm.agm_resolution_date} onChange={e => setAuditorForm(f => ({ ...f, agm_resolution_date: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Effective Appointment Date</label>
                            <input type="date" value={auditorForm.appointment_date} onChange={e => setAuditorForm(f => ({ ...f, appointment_date: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Term Ends — AGM Year</label>
                            <input value={auditorForm.term_to_year} onChange={e => setAuditorForm(f => ({ ...f, term_to_year: e.target.value }))} placeholder="e.g. 2030-31" className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">AGM Serial Label (optional)</label>
                            <input value={auditorForm.appointed_agm_serial_label} onChange={e => setAuditorForm(f => ({ ...f, appointed_agm_serial_label: e.target.value }))} placeholder="e.g. 1st AGM" className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      {/* ── Everything below mirrors e-Form ADT-1 (Sec 139, Rule 4(2)) field
                          for field, so nothing needed for an actual ADT-1 filing is missing
                          here — captured once in Company Master rather than re-typed per
                          filing. Stored in adt1_details (JSONB); appointment_category/
                          appointed_by/confirmation_status above stay the source of truth
                          for the Board Report clause. ── */}

                      <DirSection title="Appointment Classification (ADT-1 Part 3)" accent="#7c3aed" hint="Nature of appointment is the ADT-1 dropdown itself — pick the option that matches this appointment exactly.">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Falls under a class of companies u/s 139(2)?</label>
                            <select value={auditorForm.adt1_details.section139_2_applicable} onChange={e => setAdt1({ section139_2_applicable: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Nature of Appointment</label>
                            <select value={auditorForm.adt1_details.nature_of_appointment} onChange={e => setAdt1({ nature_of_appointment: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option>First auditor by Board of directors/ members/ C&AG</option>
                              <option>Appointment of Auditors in AGM</option>
                              <option>Re-appointment of Auditors in AGM</option>
                              <option>Appointment/ Re-appointment by C&AG</option>
                              <option>Auditor appointed in case of casual vacancy</option>
                              <option>Auditor appointed in case of non-re-appointment/ removal of the previous auditor</option>
                              <option>Auditor appointed by Central Government</option>
                              <option>Auditor appointed by the Tribunal</option>
                              <option>Others</option>
                            </select>
                          </div>
                          {auditorForm.adt1_details.nature_of_appointment === 'Others' && (
                            <div className="col-span-3">
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">If Others, please specify</label>
                              <input value={auditorForm.adt1_details.nature_of_appointment_other} onChange={e => setAdt1({ nature_of_appointment_other: e.target.value })} className={sic} />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Appointed at the AGM?</label>
                            <select value={auditorForm.adt1_details.appointed_in_agm} onChange={e => setAdt1({ appointed_in_agm: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          {auditorForm.adt1_details.appointed_in_agm === 'Yes' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of AGM</label>
                              <input type="date" value={auditorForm.adt1_details.agm_date} onChange={e => setAdt1({ agm_date: e.target.value })} className={sic} />
                            </div>
                          )}
                        </div>
                      </DirSection>

                      <DirSection title="Joint Auditors (ADT-1 4(b)-4(c))" accent="#0891b2" hint="Toggle on only if more than one firm/individual is being appointed together for the same audit.">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="flex items-end pb-2.5">
                            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                              <input type="checkbox" checked={auditorForm.adt1_details.is_joint_auditor} onChange={e => setAdt1({ is_joint_auditor: e.target.checked })} />
                              Joint auditors have been appointed
                            </label>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Number of Auditor(s) Appointed</label>
                            <input type="number" min="1" value={auditorForm.adt1_details.num_auditors_appointed} onChange={e => setAdt1({ num_auditors_appointed: e.target.value })} className={sic} />
                          </div>
                        </div>
                        {auditorForm.adt1_details.is_joint_auditor && (
                          <div className="rounded-xl border border-gray-200 dark:border-slate-600 p-4 bg-gray-50 dark:bg-slate-900/40">
                            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-3">Other joint auditor(s) — the primary auditor's own details are captured in the section below.</p>
                            {(auditorForm.adt1_details.joint_auditors || []).map(j => (
                              <div key={j.id} className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded-lg bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 text-xs">
                                <span className="text-gray-700 dark:text-slate-300">
                                  <span className="font-semibold text-[#0f1f45] dark:text-slate-100">{j.category === 'Firm' ? j.firm_name : j.auditor_name}</span>
                                  {j.category === 'Firm' ? ` (FRN: ${j.frn || '—'})` : ` (PAN: ${j.auditor_pan || '—'})`}
                                  {j.signing_membership_no ? ` · Signing Member No: ${j.signing_membership_no}` : ''}
                                </span>
                                <button type="button" onClick={() => removeJointAuditor(j.id)} className="text-red-500 hover:underline flex-shrink-0">Remove</button>
                              </div>
                            ))}
                            <div className="grid grid-cols-4 gap-3 mt-2">
                              <select value={jointAuditorDraft.category} onChange={e => setJointAuditorDraft(d => ({ ...d, category: e.target.value }))} className={sic}>
                                <option value="Firm">Firm</option>
                                <option value="Individual">Individual</option>
                              </select>
                              {jointAuditorDraft.category === 'Firm' ? (
                                <>
                                  <input placeholder="Firm name" value={jointAuditorDraft.firm_name} onChange={e => setJointAuditorDraft(d => ({ ...d, firm_name: e.target.value }))} className={sic} />
                                  <input placeholder="FRN" value={jointAuditorDraft.frn} onChange={e => setJointAuditorDraft(d => ({ ...d, frn: e.target.value }))} className={sic} />
                                  <input placeholder="Firm PAN" value={jointAuditorDraft.firm_pan} onChange={e => setJointAuditorDraft(d => ({ ...d, firm_pan: e.target.value.toUpperCase() }))} maxLength={10} className={sic} />
                                </>
                              ) : (
                                <>
                                  <input placeholder="Auditor name" value={jointAuditorDraft.auditor_name} onChange={e => setJointAuditorDraft(d => ({ ...d, auditor_name: e.target.value }))} className={sic} />
                                  <input placeholder="Auditor PAN" value={jointAuditorDraft.auditor_pan} onChange={e => setJointAuditorDraft(d => ({ ...d, auditor_pan: e.target.value.toUpperCase() }))} maxLength={10} className={sic} />
                                  <input placeholder="Membership No." value={jointAuditorDraft.signing_membership_no} onChange={e => setJointAuditorDraft(d => ({ ...d, signing_membership_no: e.target.value }))} className={sic} />
                                </>
                              )}
                              <input placeholder="Membership No. (signing)" value={jointAuditorDraft.signing_membership_no} onChange={e => setJointAuditorDraft(d => ({ ...d, signing_membership_no: e.target.value }))} className={sic} style={{ display: jointAuditorDraft.category === 'Firm' ? undefined : 'none' }} />
                              <input placeholder="Email" value={jointAuditorDraft.auditor_email} onChange={e => setJointAuditorDraft(d => ({ ...d, auditor_email: e.target.value }))} className={sic} />
                              <button type="button" onClick={addJointAuditor} className="px-3 py-2 rounded-xl text-xs font-semibold text-white bg-[#0f1f45] hover:bg-[#162d5e]">+ Add Joint Auditor</button>
                            </div>
                          </div>
                        )}
                      </DirSection>

                      <DirSection title="Primary Auditor — Category & Signing Person (ADT-1 4(d)-4(h))" accent="#0f1f45">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Category of Auditor</label>
                            <select value={auditorForm.adt1_details.auditor_category} onChange={e => setAdt1({ auditor_category: e.target.value })} className={sic}>
                              <option value="Firm">Auditor's Firm</option>
                              <option value="Individual">Individual</option>
                            </select>
                          </div>
                          {auditorForm.adt1_details.auditor_category === 'Firm' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Income Tax PAN of the Firm</label>
                              <input value={auditorForm.adt1_details.firm_pan} onChange={e => setAdt1({ firm_pan: e.target.value.toUpperCase() })} maxLength={10} className={sic} />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Membership No. of Auditor signing the Balance Sheet</label>
                            <input value={auditorForm.adt1_details.signing_membership_no} onChange={e => setAdt1({ signing_membership_no: e.target.value })} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Name of the Auditor {auditorForm.adt1_details.auditor_category === 'Individual' ? '' : '(signing partner)'}</label>
                            <input value={auditorForm.adt1_details.auditor_name} onChange={e => setAdt1({ auditor_name: e.target.value })} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Income Tax PAN of the Auditor</label>
                            <input value={auditorForm.adt1_details.auditor_pan} onChange={e => setAdt1({ auditor_pan: e.target.value.toUpperCase() })} maxLength={10} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Email ID</label>
                            <input type="email" value={auditorForm.adt1_details.auditor_email} onChange={e => setAdt1({ auditor_email: e.target.value })} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Mobile Number</label>
                            <input value={auditorForm.adt1_details.auditor_mobile} onChange={e => setAdt1({ auditor_mobile: e.target.value })} className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      <DirSection title="Registered Addresses (ADT-1 4(f)(ii) / 4(h)(ii))" accent="#64748b">
                        <div className="grid grid-cols-2 gap-6">
                          {auditorForm.adt1_details.auditor_category === 'Firm' && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2">Address of the Auditor's Firm</p>
                              <div className="grid grid-cols-2 gap-2">
                                <input placeholder="Address Line 1" value={auditorForm.adt1_details.firm_address.line1} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, line1: e.target.value } })} className={`${sic} col-span-2`} />
                                <input placeholder="Address Line 2" value={auditorForm.adt1_details.firm_address.line2} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, line2: e.target.value } })} className={`${sic} col-span-2`} />
                                <input placeholder="Area / Locality" value={auditorForm.adt1_details.firm_address.area} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, area: e.target.value } })} className={sic} />
                                <input placeholder="City" value={auditorForm.adt1_details.firm_address.city} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, city: e.target.value } })} className={sic} />
                                <input placeholder="District" value={auditorForm.adt1_details.firm_address.district} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, district: e.target.value } })} className={sic} />
                                <input placeholder="State/UT" value={auditorForm.adt1_details.firm_address.state} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, state: e.target.value } })} className={sic} />
                                <input placeholder="Pin Code" value={auditorForm.adt1_details.firm_address.pincode} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, pincode: e.target.value } })} className={sic} />
                                <input placeholder="Country" value={auditorForm.adt1_details.firm_address.country} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, country: e.target.value } })} className={sic} />
                                <input type="email" placeholder="Firm Email ID" value={auditorForm.adt1_details.firm_address.email} onChange={e => setAdt1({ firm_address: { ...auditorForm.adt1_details.firm_address, email: e.target.value } })} className={`${sic} col-span-2`} />
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-2">Address of the Auditor {auditorForm.adt1_details.auditor_category === 'Firm' ? '(signing partner)' : ''}</p>
                            <div className="grid grid-cols-2 gap-2">
                              <input placeholder="Address Line 1" value={auditorForm.adt1_details.auditor_address.line1} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, line1: e.target.value } })} className={`${sic} col-span-2`} />
                              <input placeholder="Address Line 2" value={auditorForm.adt1_details.auditor_address.line2} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, line2: e.target.value } })} className={`${sic} col-span-2`} />
                              <input placeholder="Area / Locality" value={auditorForm.adt1_details.auditor_address.area} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, area: e.target.value } })} className={sic} />
                              <input placeholder="City" value={auditorForm.adt1_details.auditor_address.city} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, city: e.target.value } })} className={sic} />
                              <input placeholder="District" value={auditorForm.adt1_details.auditor_address.district} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, district: e.target.value } })} className={sic} />
                              <input placeholder="State/UT" value={auditorForm.adt1_details.auditor_address.state} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, state: e.target.value } })} className={sic} />
                              <input placeholder="Pin Code" value={auditorForm.adt1_details.auditor_address.pincode} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, pincode: e.target.value } })} className={sic} />
                              <input placeholder="Country" value={auditorForm.adt1_details.auditor_address.country} onChange={e => setAdt1({ auditor_address: { ...auditorForm.adt1_details.auditor_address, country: e.target.value } })} className={sic} />
                            </div>
                          </div>
                        </div>
                      </DirSection>

                      <DirSection title="Period of Appointment & Eligibility (ADT-1 4(i)-4(l))" accent="#d97706" hint="Number of financial years is auto-calculated FY block-wise (April–March) from the period — not a raw date difference.">
                        <div className="grid grid-cols-4 gap-4 mb-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Period From</label>
                            <input type="date" value={auditorForm.adt1_details.period_from} onChange={e => setAdt1({ period_from: e.target.value })} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Period To</label>
                            <input type="date" value={auditorForm.adt1_details.period_to} onChange={e => setAdt1({ period_to: e.target.value })} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">No. of Financial Year(s)</label>
                            <div className={`${sic} bg-gray-50 dark:bg-slate-900/40 flex items-center text-gray-700 dark:text-slate-300 font-semibold`}>
                              {computeFyCount(auditorForm.adt1_details.period_from, auditorForm.adt1_details.period_to) ?? '—'}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Within the limit of 20 companies u/s 141(3)(g)?</label>
                            <select value={auditorForm.adt1_details.within_20_company_limit} onChange={e => setAdt1({ within_20_company_limit: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Previously conducted audit / was in this company (Rule 6, excl. 5+ yr break)?</label>
                            <select value={auditorForm.adt1_details.prior_association} onChange={e => setAdt1({ prior_association: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                            </select>
                          </div>
                        </div>
                      </DirSection>

                      {auditorForm.adt1_details.prior_association === 'Yes' && (
                        <DirSection title="Prior Tenure at this Company (ADT-1 4(m)-4(s))" accent="#d97706" hint="One row per earlier stint of this auditor/firm/member at this company.">
                          {(auditorForm.adt1_details.prior_tenure || []).map(row => (
                            <div key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded-lg bg-gray-50 dark:bg-slate-900/40 border border-gray-100 dark:border-slate-700 text-xs">
                              <span className="text-gray-700 dark:text-slate-300">
                                #{row.s_no} · FRN: {row.frn || '—'} · Membership No: {row.membership_no || '—'} · FY {row.fy_start || '—'} to {row.fy_end || '—'}
                              </span>
                              <button type="button" onClick={() => removePriorTenureRow(row.id)} className="text-red-500 hover:underline flex-shrink-0">Remove</button>
                            </div>
                          ))}
                          <div className="grid grid-cols-5 gap-2 mt-2">
                            <input placeholder="S.No." value={priorTenureDraft.s_no} onChange={e => setPriorTenureDraft(d => ({ ...d, s_no: e.target.value }))} className={sic} />
                            <input placeholder="FRN" value={priorTenureDraft.frn} onChange={e => setPriorTenureDraft(d => ({ ...d, frn: e.target.value }))} className={sic} />
                            <input placeholder="Membership No." value={priorTenureDraft.membership_no} onChange={e => setPriorTenureDraft(d => ({ ...d, membership_no: e.target.value }))} className={sic} />
                            <input placeholder="FY Start (e.g. 2019-20)" value={priorTenureDraft.fy_start} onChange={e => setPriorTenureDraft(d => ({ ...d, fy_start: e.target.value }))} className={sic} />
                            <input placeholder="FY End (e.g. 2021-22)" value={priorTenureDraft.fy_end} onChange={e => setPriorTenureDraft(d => ({ ...d, fy_end: e.target.value }))} className={sic} />
                          </div>
                          <button type="button" onClick={addPriorTenureRow} className="mt-2 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-[#0f1f45] hover:bg-[#162d5e]">+ Add Prior Tenure Row</button>
                        </DirSection>
                      )}

                      <DirSection title="Audit Committee & Tribunal (ADT-1 Part 5-6)" accent="#16a34a">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Audit Committee (Sec 177) recommendation considered by Board?</label>
                            <select value={auditorForm.adt1_details.audit_committee_recommendation} onChange={e => setAdt1({ audit_committee_recommendation: e.target.value })} className={sic}>
                              <option value="">—</option>
                              <option value="Yes">Yes</option>
                              <option value="No">No</option>
                              <option value="Not Applicable">Not Applicable</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">SRN of INC-28 (Tribunal order for appointment, if any)</label>
                            <input value={auditorForm.adt1_details.tribunal_order_srn} onChange={e => setAdt1({ tribunal_order_srn: e.target.value })} className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      {(auditorForm.adt1_details.nature_of_appointment === 'Auditor appointed in case of casual vacancy' || auditorForm.appointment_category === 'Casual Vacancy') && (
                        <DirSection title="Casual Vacancy Disclosure (ADT-1 Part 7)" accent="#dc2626" hint="Discloses the OUTGOING auditor who vacated the office — not this incoming auditor's own details.">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Casual vacancy caused by</label>
                              <select value={auditorForm.adt1_details.casual_vacancy.reason} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, reason: e.target.value } })} className={sic}>
                                <option value="">—</option>
                                <option value="Resignation">Resignation</option>
                                <option value="Others">Others (death, disqualification)</option>
                              </select>
                            </div>
                            {auditorForm.adt1_details.casual_vacancy.reason === 'Others' && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">If Others, please specify</label>
                                <input value={auditorForm.adt1_details.casual_vacancy.reason_other} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, reason_other: e.target.value } })} className={sic} />
                              </div>
                            )}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">SRN of relevant form (e.g. outgoing auditor's ADT-3)</label>
                              <input value={auditorForm.adt1_details.casual_vacancy.srn} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, srn: e.target.value } })} className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Casual Vacancy</label>
                              <input type="date" value={auditorForm.adt1_details.casual_vacancy.date} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, date: e.target.value } })} className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">FRN of Auditor's Firm who vacated</label>
                              <input value={auditorForm.adt1_details.casual_vacancy.vacated_frn} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, vacated_frn: e.target.value } })} className={sic} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Membership No. of Auditor who vacated</label>
                              <input value={auditorForm.adt1_details.casual_vacancy.vacated_membership_no} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, vacated_membership_no: e.target.value } })} className={sic} />
                            </div>
                            <div className="col-span-3">
                              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Reasons of the Casual Vacancy</label>
                              <textarea rows={2} value={auditorForm.adt1_details.casual_vacancy.reasons_text} onChange={e => setAdt1({ casual_vacancy: { ...auditorForm.adt1_details.casual_vacancy, reasons_text: e.target.value } })} className={sic} />
                            </div>
                          </div>
                        </DirSection>
                      )}

                      <DirSection title="This Auditor's Own ADT-1 Filing" accent="#16a34a">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="flex items-end pb-2.5">
                            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300">
                              <input type="checkbox" checked={auditorForm.form_adt1_filed} onChange={e => setAuditorForm(f => ({ ...f, form_adt1_filed: e.target.checked }))} />
                              ADT-1 Filed
                            </label>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">SRN</label>
                            <input value={auditorForm.form_adt1_srn} onChange={e => setAuditorForm(f => ({ ...f, form_adt1_srn: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Filing Date</label>
                            <input type="date" value={auditorForm.form_adt1_date} onChange={e => setAuditorForm(f => ({ ...f, form_adt1_date: e.target.value }))} className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      <DirSection title="Cessation" accent="#dc2626" hint="Fill only if this auditor has resigned, been removed, or completed their term.">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Cessation Date</label>
                            <input type="date" value={auditorForm.cessation_date} onChange={e => setAuditorForm(f => ({ ...f, cessation_date: e.target.value }))} className={sic} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Reason</label>
                            <select value={auditorForm.cessation_reason} onChange={e => setAuditorForm(f => ({ ...f, cessation_reason: e.target.value }))} className={sic}>
                              <option value="">—</option>
                              <option value="Resignation">Resignation</option>
                              <option value="Removed under Section 140(1)">Removed under Section 140(1)</option>
                              <option value="Term Completed / Replaced">Term Completed / Replaced</option>
                              <option value="Death">Death</option>
                              <option value="Disqualification">Disqualification</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Cessation Resolution Date</label>
                            <input type="date" value={auditorForm.cessation_resolution_date} onChange={e => setAuditorForm(f => ({ ...f, cessation_resolution_date: e.target.value }))} className={sic} />
                          </div>
                        </div>
                      </DirSection>

                      <DirSection title="Notes">
                        <textarea rows={2} value={auditorForm.notes} onChange={e => setAuditorForm(f => ({ ...f, notes: e.target.value }))} className={sic} />
                      </DirSection>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 flex items-center justify-end gap-3 flex-shrink-0">
                      <button type="button"
                        onClick={() => { setShowAuditorForm(false); setEditingAuditorId(null); setAuditorForm(EMPTY_AUDITOR); }}
                        className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition">
                        Cancel
                      </button>
                      <button type="submit" disabled={savingAuditor}
                        className="px-5 py-2 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] disabled:opacity-60 transition shadow-sm">
                        {savingAuditor ? 'Saving…' : editingAuditorId ? 'Save Changes' : 'Add Auditor'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Auditors List */}
              {auditorsLoading ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">Loading auditors...</div>
              ) : auditors.length === 0 && !showAuditorForm ? (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-500 dark:text-slate-400">No auditor records yet</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Add one manually, or pass a Passed auditor-appointment resolution in Event Filing / Annual Filing to auto-fill this</p>
                </div>
              ) : auditors.length > 0 && (
                <div className="p-6 grid grid-cols-1 gap-3">
                  {auditors.map(a => (
                    <div key={a.id} className={`relative rounded-2xl border overflow-hidden transition-all ${
                      a.is_active
                        ? 'border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-100 dark:hover:border-blue-900 hover:shadow-sm'
                        : 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 opacity-70'
                    }`}>
                      <div className="px-5 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${a.is_active ? 'text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}
                            style={a.is_active ? { background: '#00206E' } : {}}>
                            {a.firm_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100 truncate">{a.firm_name}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${
                                a.is_active
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900'
                                  : 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900'
                              }`}>
                                {a.is_active ? 'Active' : 'Ceased'}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 border border-blue-100 flex-shrink-0">
                                {a.appointment_category}
                              </span>
                              {a.confirmation_status === 'Pending EGM Confirmation' && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-900 flex-shrink-0">
                                  Pending EGM Confirmation
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                              {a.frn ? `FRN: ${a.frn}` : 'FRN not added'}
                              {a.term_to_year ? ` · Term to AGM ${a.term_to_year}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 flex-shrink-0">
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">Appointed</p>
                            <p className="text-xs font-semibold text-gray-700 dark:text-slate-300">
                              {a.appointment_date ? new Date(a.appointment_date).toLocaleDateString('en-IN') : '-'}
                            </p>
                          </div>
                          <div className="text-right hidden md:block">
                            <p className="text-xs text-gray-400 dark:text-slate-500">ADT-1</p>
                            <span className={`text-xs font-semibold ${a.form_adt1_filed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                              {a.form_adt1_filed ? '✓ Filed' : 'Pending'}
                            </span>
                          </div>
                          {!a.is_active && (
                            <div className="text-right hidden md:block">
                              <p className="text-xs text-gray-400 dark:text-slate-500">Ceased</p>
                              <p className="text-xs font-semibold text-gray-700 dark:text-slate-300">
                                {a.cessation_date ? new Date(a.cessation_date).toLocaleDateString('en-IN') : '-'}{a.cessation_reason ? ` · ${a.cessation_reason}` : ''}
                              </p>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => handleEditAuditor(a)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-blue-50 dark:hover:bg-slate-700 hover:text-blue-600 transition">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button type="button" onClick={() => handleDeleteAuditor(a)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-500 transition">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── CAPITAL CONTRIBUTION TAB — LLP variant (single inline-editable table) ── */}
          {detailTab === 'shareholders' && isLLPView && (() => {
            const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
            const partners = directors.filter(d => d.is_active);
            const valOf = (d, key, col) => {
              const e = contribEdits[d.id] || {};
              return e[key] !== undefined ? e[key] : (d[col] ?? '');
            };
            const setVal = (id, key, v) => setContribEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: v } }));
            const rows = partners.map(d => {
              const obligation = Number(valOf(d, 'obligation', 'contribution_obligation')) || 0;
              const received = Number(valOf(d, 'received', 'contribution_received')) || 0;
              const profit = Number(valOf(d, 'profit', 'profit_share_percent')) || 0;
              return { d, obligation, received, profit, shortfall: Math.max(0, obligation - received), dirty: !!contribEdits[d.id] };
            });
            const totObl = rows.reduce((s, r) => s + r.obligation, 0);
            const totRec = rows.reduce((s, r) => s + r.received, 0);
            const totProfit = rows.reduce((s, r) => s + r.profit, 0);
            return (
            <div>
              {/* Toolbar */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">Contribution Register</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{E.capital.registerNote}</p>
                </div>
                <button onClick={handleSyncPeople} disabled={syncingPeople}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-60 transition">
                  {syncingPeople ? 'Syncing…' : 'Sync to People Master'}
                </button>
              </div>

              <p className="px-6 pt-4 text-xs text-gray-500 dark:text-slate-400">
                One row per partner. Edit <span className="font-semibold">Obligation</span>, <span className="font-semibold">Contribution Received</span> or <span className="font-semibold">Profit&nbsp;Share&nbsp;%</span> inline and press <span className="font-semibold">Save</span> on that row. These values feed the Overview snapshot and Form&nbsp;11&nbsp;/&nbsp;Form&nbsp;8.
              </p>

              {directorsLoading ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">Loading contribution register…</div>
              ) : partners.length === 0 ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">No partners added yet. Add partners in the Partners tab first.</div>
              ) : (
                <div className="px-6 py-5">
                  <div className="rounded-2xl border border-gray-100 dark:border-slate-700 overflow-x-auto">
                    <table className="w-full text-sm min-w-[720px]">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-900 text-[11px] uppercase text-gray-500 dark:text-slate-400">
                          {['Partner', 'PAN', 'Obligation to Contribute (₹)', 'Contribution Received (₹)', 'Shortfall', 'Profit Share %', ''].map(h => (
                            <th key={h} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(({ d, obligation, received, profit, shortfall, dirty }) => (
                          <tr key={d.id} className="border-t border-gray-50 dark:border-slate-800">
                            <td className="px-4 py-2.5 font-semibold text-gray-800 dark:text-slate-200 whitespace-nowrap">
                              {d.director_name}
                              {d.is_designated_partner && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300">DP</span>}
                            </td>
                            <td className="px-4 py-2.5 text-xs font-mono text-gray-500 dark:text-slate-400">{d.pan || '-'}</td>
                            <td className="px-4 py-2">
                              <input type="number" min="0" className={`${sic} !py-1.5 max-w-[150px]`}
                                value={valOf(d, 'obligation', 'contribution_obligation')}
                                onChange={e => setVal(d.id, 'obligation', e.target.value)} />
                            </td>
                            <td className="px-4 py-2">
                              <input type="number" min="0" className={`${sic} !py-1.5 max-w-[150px]`}
                                value={valOf(d, 'received', 'contribution_received')}
                                onChange={e => setVal(d.id, 'received', e.target.value)} />
                            </td>
                            <td className={`px-4 py-2.5 whitespace-nowrap ${shortfall > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>
                              {shortfall > 0 ? inr(shortfall) : '—'}
                            </td>
                            <td className="px-4 py-2">
                              <input type="number" min="0" step="0.01" className={`${sic} !py-1.5 max-w-[90px]`}
                                value={valOf(d, 'profit', 'profit_share_percent')}
                                onChange={e => setVal(d.id, 'profit', e.target.value)} />
                            </td>
                            <td className="px-4 py-2.5">
                              <button type="button" onClick={() => handleSavePartnerContrib(d)}
                                disabled={!dirty || contribSavingId === d.id}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0f1f45] text-white hover:bg-[#162d5e] disabled:opacity-40 transition">
                                {contribSavingId === d.id ? 'Saving…' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-900/40 font-bold text-[#0f1f45] dark:text-slate-100">
                          <td className="px-4 py-2.5" colSpan={2}>Total</td>
                          <td className="px-4 py-2.5">{inr(totObl)}</td>
                          <td className="px-4 py-2.5 text-emerald-700 dark:text-emerald-400">{inr(totRec)}</td>
                          <td className="px-4 py-2.5">{totObl - totRec > 0 ? inr(totObl - totRec) : '—'}</td>
                          <td className="px-4 py-2.5">{totProfit}%</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {totProfit !== 0 && totProfit !== 100 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2">Profit-share percentages total {totProfit}% — they should sum to 100% per the LLP Agreement.</p>
                  )}
                </div>
              )}
            </div>
            );
          })()}

          {detailTab === 'shareholders' && !isLLPView && (
            <div>
              {/* Toolbar */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">
                    Register of Members
                  </p>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                    {shareholders.length} {shareholders.length === 1 ? 'entry' : 'entries'} · Statutory format as per Companies Act 2013
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSyncPeople}
                    disabled={syncingPeople}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-60 transition"
                  >
                    {syncingPeople ? 'Syncing…' : 'Sync to People Master'}
                  </button>
                  <button
                    onClick={() => {
                      setShForm(EMPTY_SH);
                      setShSource('manual');
                      setEditingShareholderId(null);
                      setShowShareholderForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] transition shadow-sm"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Entry
                  </button>
                </div>
              </div>

              {/* Shareholder Form */}
              {showShareholderForm && (
                <form onSubmit={handleSaveShareholder} className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">

                  {/* Form Header */}
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-600 flex items-center justify-between bg-white dark:bg-slate-800">
                    <div>
                      <p className="text-sm font-bold text-[#0f1f45] dark:text-slate-100">
                        {editingShareholderId ? 'Edit Shareholder Entry' : 'New Shareholder Entry'}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">Fill statutory details for Register of Members</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setShowShareholderForm(false); setEditingShareholderId(null); setShForm(EMPTY_SH); }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>

                  <div className="p-6 space-y-6">

                    {/* Section 1 — Member Identity */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">Member Identity</p>

                      {/* Source selector */}
                      <div className="flex items-center gap-1 mb-3">
                        {[
                          { key: 'manual', label: 'Manual entry' },
                          { key: 'people', label: 'Link from People' },
                          { key: 'director', label: 'Link from Director' },
                        ].map(opt => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => {
                              setShSource(opt.key);
                              setShForm(f => ({ ...f, person_id: '', director_ref_id: '' }));
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                              shSource === opt.key
                                ? 'border-[#00206E] text-[#00206E] bg-blue-50 dark:bg-blue-950/40'
                                : 'border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                        {shSource === 'people' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Person</label>
                            <select
                              value={shForm.person_id}
                              onChange={e => handleShPersonSelect(e.target.value)}
                              className={sic}
                            >
                              <option value="">Select person</option>
                              {persons.map(p => (
                                <option key={p.id} value={p.id}>{p.full_name}{p.pan_number ? ` · ${p.pan_number}` : ''}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {shSource === 'director' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Director</label>
                            <select
                              value={shForm.director_ref_id}
                              onChange={e => handleShDirectorSelect(e.target.value)}
                              className={sic}
                            >
                              <option value="">Select director</option>
                              {directors.map(d => (
                                <option key={d.id} value={d.id}>{d.director_name}{d.pan ? ` · ${d.pan}` : ''}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Member Name *</label>
                          <input
                            required
                            value={shForm.member_name}
                            onChange={e => setShForm(f => ({ ...f, member_name: e.target.value }))}
                            placeholder="Full legal name"
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Father / Husband Name</label>
                          <input
                            value={shForm.father_husband_name}
                            onChange={e => setShForm(f => ({ ...f, father_husband_name: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">PAN</label>
                          <input
                            value={shForm.pan}
                            onChange={e => setShForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Aadhaar</label>
                          <input
                            value={shForm.aadhaar}
                            onChange={e => setShForm(f => ({ ...f, aadhaar: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Email</label>
                          <input
                            type="email"
                            value={shForm.email}
                            onChange={e => setShForm(f => ({ ...f, email: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Address</label>
                          <input
                            value={shForm.address}
                            onChange={e => setShForm(f => ({ ...f, address: e.target.value }))}
                            className={sic}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2 — Share Details */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">Share Details</p>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Folio Number</label>
                          <input
                            value={shForm.folio_number}
                            onChange={e => setShForm(f => ({ ...f, folio_number: e.target.value }))}
                            placeholder="e.g. F001"
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Share Class</label>
                          <select
                            value={shForm.share_class}
                            onChange={e => setShForm(f => ({ ...f, share_class: e.target.value }))}
                            className={sic}
                          >
                            <option>Equity</option>
                            <option>Preference-A</option>
                            <option>Preference-B</option>
                            <option>Compulsorily Convertible Preference</option>
                            <option>Optionally Convertible Preference</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Distinctive No. From</label>
                          <input
                            type="number"
                            value={shForm.distinctive_from}
                            onChange={e => setShForm(f => ({ ...f, distinctive_from: e.target.value }))}
                            placeholder="1"
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Distinctive No. To</label>
                          <input
                            type="number"
                            value={shForm.distinctive_to}
                            onChange={e => setShForm(f => ({ ...f, distinctive_to: e.target.value }))}
                            placeholder="1000"
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">No. of Shares *</label>
                          <input
                            required
                            type="number"
                            value={shForm.no_of_shares}
                            onChange={e => setShForm(f => ({ ...f, no_of_shares: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Face Value (₹)</label>
                          <input
                            type="number"
                            value={shForm.face_value}
                            onChange={e => setShForm(f => ({ ...f, face_value: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Amount Paid (₹)</label>
                          <input
                            type="number"
                            value={shForm.amount_paid}
                            onChange={e => setShForm(f => ({ ...f, amount_paid: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Total Amount (₹)</label>
                          <div className="w-full border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-slate-300 font-semibold">
                            ₹{((Number(shForm.no_of_shares) || 0) * (Number(shForm.amount_paid) || 0)).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 3 — Acquisition */}
                    {(() => {
                      const incDate = selectedCompany.incorporation_date ? String(selectedCompany.incorporation_date).slice(0, 10) : '';
                      const incFmt = selectedCompany.incorporation_date ? new Date(selectedCompany.incorporation_date).toLocaleDateString('en-IN') : '—';
                      const mode = shForm.mode_of_acquisition;
                      const isSub = mode === 'Subscription to Memorandum';
                      const isTransfer = mode === 'Transfer';
                      const isTransmission = mode === 'Transmission';
                      const ACQ_HELP = {
                        'Subscription to Memorandum': `Subscriber to the MOA — shares are deemed allotted on the date of incorporation (${incFmt}). No PAS-3; the person's name goes into the Register of Members on incorporation. Amount paid is the subscription amount agreed in the MOA.`,
                        'Fresh Allotment': 'A fresh issue of shares after incorporation by Board/members resolution. File PAS-3 within 30 days of allotment. Date of Allotment = the allotment (Board resolution) date.',
                        'Rights Issue': 'Allotment under Sec 62(1)(a) to existing members in proportion to holding. Treat like Fresh Allotment — PAS-3, Date of Allotment = allotment date.',
                        'Bonus Issue': 'Capitalisation of reserves under Sec 63 — no money received (amount paid = face value, funded from reserves). PAS-3, Date of Allotment = allotment date.',
                        'ESOP': 'Allotment on exercise of options under an approved ESOP scheme (Sec 62(1)(b)). PAS-3, Date of Allotment = exercise/allotment date.',
                        'Transfer': 'Shares bought from an existing member via SH-4. Record the SH-4 / transfer in the Share Transfers tab first — here, "Date of Allotment" is the date the transfer was registered by the Board.',
                        'Transmission': 'Shares passing by operation of law (death / insolvency). No SH-4 / no stamp duty. "Date of Allotment" = date the Board registered the transmission on production of succession documents.',
                      };
                      return (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">Acquisition Details</p>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Mode of Acquisition</label>
                          <select
                            value={shForm.mode_of_acquisition}
                            onChange={e => {
                              const v = e.target.value;
                              setShForm(f => ({
                                ...f,
                                mode_of_acquisition: v,
                                // Subscribers: allotment + register-entry dates default to incorporation date.
                                date_of_allotment: v === 'Subscription to Memorandum' ? (incDate || f.date_of_allotment) : f.date_of_allotment,
                                date_of_entry: v === 'Subscription to Memorandum' ? (incDate || f.date_of_entry) : f.date_of_entry,
                              }));
                            }}
                            className={sic}
                          >
                            <option>Subscription to Memorandum</option>
                            <option>Fresh Allotment</option>
                            <option>Rights Issue</option>
                            <option>Bonus Issue</option>
                            <option>ESOP</option>
                            <option>Transfer</option>
                            <option>Transmission</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                            {isSub ? 'Date of Allotment (= Incorporation)' : isTransfer ? 'Date of Transfer Registered' : isTransmission ? 'Date of Transmission Registered' : 'Date of Allotment'}
                          </label>
                          <input
                            type="date"
                            value={shForm.date_of_allotment}
                            onChange={e => setShForm(f => ({ ...f, date_of_allotment: e.target.value }))}
                            className={sic}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Entry in Register</label>
                          <input
                            type="date"
                            value={shForm.date_of_entry}
                            onChange={e => setShForm(f => ({ ...f, date_of_entry: e.target.value }))}
                            className={sic}
                          />
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Sec 88 — date this entry was actually made in the Register of Members (MGT-1).</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Status</label>
                          <select
                            value={shForm.is_active ? 'active' : 'inactive'}
                            onChange={e => setShForm(f => ({ ...f, is_active: e.target.value === 'active' }))}
                            className={sic}
                          >
                            <option value="active">Active (current member)</option>
                            <option value="inactive">Ceased (fully transferred / bought back)</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-3 leading-relaxed rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900 px-3 py-2">
                        {ACQ_HELP[mode] || ''}
                      </p>
                    </div>
                      );
                    })()}

                    {/* Section 4 — Cessation (conditional) */}
                    {!shForm.is_active && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">Cessation Details</p>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Date of Cessation</label>
                            <input
                              type="date"
                              value={shForm.date_of_cessation}
                              onChange={e => setShForm(f => ({ ...f, date_of_cessation: e.target.value }))}
                              className={sic}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Reason</label>
                            <select
                              value={shForm.cessation_reason}
                              onChange={e => setShForm(f => ({ ...f, cessation_reason: e.target.value }))}
                              className={sic}
                            >
                              <option value="">Select reason</option>
                              <option>Transfer</option>
                              <option>Transmission</option>
                              <option>Buyback</option>
                              <option>Death</option>
                              <option>Redemption</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Transferred To</label>
                            <input
                              value={shForm.transferred_to}
                              onChange={e => setShForm(f => ({ ...f, transferred_to: e.target.value }))}
                              placeholder="Name of transferee"
                              className={sic}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Notes</label>
                      <input
                        value={shForm.notes}
                        onChange={e => setShForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Internal notes"
                        className={sic}
                      />
                    </div>
                  </div>

                  {/* Form Footer */}
                  <div className="px-6 py-4 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowShareholderForm(false); setEditingShareholderId(null); setShForm(EMPTY_SH); }}
                      className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingShareholder}
                      className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] disabled:opacity-60 transition"
                    >
                      {savingShareholder ? 'Saving...' : editingShareholderId ? 'Update Entry' : 'Save Entry'}
                    </button>
                  </div>
                </form>
              )}

              {/* Shareholders Table */}
              {shareholdersLoading ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">Loading register...</div>
              ) : shareholders.length === 0 ? (
                <div className="py-16 text-center text-gray-400 dark:text-slate-500 text-sm">
                  No entries in Register of Members yet.
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                      {['Folio', 'Member Name', 'PAN', 'Share Class', 'Shares', 'Face Value', 'Total Amount', 'Mode', 'Date', 'Status', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {shareholders.map(sh => (
                      <tr key={sh.id} className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-slate-700">
                        <td className="px-4 py-3 text-xs font-mono text-gray-500 dark:text-slate-400">{sh.folio_number || '-'}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">{sh.member_name}</p>
                          <p className="text-xs text-gray-400 dark:text-slate-500">{sh.father_husband_name || ''}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-300">{sh.pan || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 border border-blue-100">
                            {sh.share_class}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800 dark:text-slate-200">
                          {Number(sh.no_of_shares).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">₹{sh.face_value}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800 dark:text-slate-200">
                          ₹{Number(sh.total_amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-300">{sh.mode_of_acquisition}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-300">
                          {sh.date_of_allotment ? new Date(sh.date_of_allotment).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                            sh.is_active
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900'
                              : 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900'
                          }`}>
                            {sh.is_active ? 'Active' : 'Ceased'}
                          </span>
                        </td>
                        <td className="px-4 py-3 relative">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditShareholder(sh)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteShareholder(sh)}
                              className="text-xs text-red-500 dark:text-red-400 hover:underline font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── SHARE TRANSFERS TAB (reused from Registers.js) ── */}
          {detailTab === 'transfers' && (
            <div className="p-6"><TransfersRegister company={selectedCompany} /></div>
          )}

          {/* ── BOARD / PARTNERS' MEETINGS TAB ──
              Companies → Board Meeting Register (Registers.js).
              LLPs → Partners' Meetings, stored in the shared mca_meetings
              table and owned by the LLP Register, embedded here so the
              master view and the LLP Register share one store. */}
          {detailTab === 'meetings' && (
            <div className="p-6">
              {isLLPView
                ? <PartnersMeetings company={selectedCompany} embedded />
                : <BoardMeetingRegister company={selectedCompany} entity={E} />}
            </div>
          )}

          {/* ── GENERAL MEETINGS TAB (reused from Registers.js) ── */}
          {detailTab === 'general-meetings' && (
            <div className="p-6"><GeneralMeetingRegister company={selectedCompany} /></div>
          )}

          {/* ── COMMITTEE MEETINGS TAB (reused from Registers.js) ── */}
          {detailTab === 'committees' && (
            <div className="p-6"><CommitteeMeetingRegister company={selectedCompany} /></div>
          )}

          {/* ── CHARGES TAB (reused from Registers.js) ── */}
          {detailTab === 'charges' && (
            <div className="p-6"><ChargesRegister company={selectedCompany} entity={E} /></div>
          )}

          {/* ── CONTRACTS / AOC-2 TAB (reused from Registers.js) ── */}
          {detailTab === 'contracts' && (
            <div className="p-6"><ContractsRegister company={selectedCompany} /></div>
          )}

          {/* ── FILINGS TAB ── */}
          {detailTab === 'filings' && (
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-sm font-bold text-[#0f1f45] dark:text-slate-100 mb-3">Event Filings</h4>
                {filingsLoading ? (
                  <div className="py-10 text-center text-gray-400 dark:text-slate-500 text-sm">Loading…</div>
                ) : filings.length === 0 ? (
                  <div className="py-10 text-center text-gray-400 dark:text-slate-500 text-sm border border-dashed border-gray-200 dark:border-slate-700 rounded-xl">
                    No event filings yet for this company.
                  </div>
                ) : (
                  <div className="border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-900/40">
                          {['Case', 'Event', 'Forms', 'Status', 'Created'].map(h => (
                            <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filings.map(f => {
                          const statusColor = FILING_STATUS_COLORS[f.status] || 'bg-gray-100 text-gray-600';
                          return (
                            <tr key={f.id} onClick={() => onNavigate && onNavigate('event-filings', { caseId: f.id })}
                              className="border-b border-gray-50 dark:border-slate-800 hover:bg-gray-50/60 dark:hover:bg-slate-900/40 cursor-pointer">
                              <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-slate-200">{f.title}</td>
                              <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">{f.event_name}</td>
                              <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400 font-mono">{(f.filings || []).map(x => x.form).join(', ') || '—'}</td>
                              <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusColor}`}>{f.status}</span></td>
                              <td className="px-4 py-3 text-xs text-gray-400 dark:text-slate-500">{f.created_at ? new Date(f.created_at).toLocaleDateString('en-IN') : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-bold text-[#0f1f45] dark:text-slate-100 mb-1">Annual Filings</h4>
                {isLLPView && (
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">LLP annual filing: <b>Form 11</b> (Annual Return) by 30 May · <b>Form 8</b> (Statement of Account &amp; Solvency) by 30 October. Audit required if turnover &gt; ₹40 L or contribution &gt; ₹25 L.</p>
                )}
                {annualFilingsLoading ? (
                  <div className="py-10 text-center text-gray-400 dark:text-slate-500 text-sm">Loading…</div>
                ) : annualFilings.length === 0 ? (
                  <div className="py-10 text-center text-gray-400 dark:text-slate-500 text-sm border border-dashed border-gray-200 dark:border-slate-700 rounded-xl">
                    No annual filing cases yet for this company.
                  </div>
                ) : (
                  <div className="border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-900/40">
                          {['Financial Year', 'Forms', 'Status', 'Synced'].map(h => (
                            <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {annualFilings.map(af => {
                          const statusColor = FILING_STATUS_COLORS[af.status] || 'bg-gray-100 text-gray-600';
                          return (
                            <tr key={af.id} onClick={() => onNavigate && onNavigate('annual-filings', { caseId: af.id })}
                              className="border-b border-gray-50 dark:border-slate-800 hover:bg-gray-50/60 dark:hover:bg-slate-900/40 cursor-pointer">
                              <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-slate-200">FY {af.financial_year}</td>
                              <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400 font-mono">{(af.filings || []).map(x => x.form).join(', ') || '—'}</td>
                              <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusColor}`}>{af.status}</span></td>
                              <td className="px-4 py-3 text-xs text-gray-400 dark:text-slate-500">{af.synced_at ? new Date(af.synced_at).toLocaleDateString('en-IN') : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <style>{`
        .dark [class~="bg-white"] { background-color: #1e293b !important; }
        .dark [class~="bg-gray-50"] { background-color: #0f172a !important; }
        .dark [class~="bg-gray-100"] { background-color: #334155 !important; }
        .dark [class~="bg-gray-200"] { background-color: #475569 !important; }
        .dark [class~="bg-blue-50"] { background-color: rgba(30,58,138,0.3) !important; }
        .dark [class~="bg-red-50"] { background-color: rgba(127,29,29,0.3) !important; }
        .dark [class~="bg-emerald-50"] { background-color: rgba(6,78,59,0.3) !important; }
        .dark [class~="bg-amber-50"] { background-color: rgba(120,53,15,0.3) !important; }

        .dark [class~="hover:bg-gray-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-gray-100"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-blue-50"]:hover { background-color: #334155 !important; }
        .dark [class~="hover:bg-red-50"]:hover { background-color: rgba(127,29,29,0.4) !important; }
        .dark [class~="hover:bg-white"]:hover { background-color: #334155 !important; }

        .dark [class~="text-gray-400"] { color: #64748b !important; }
        .dark [class~="text-gray-500"] { color: #94a3b8 !important; }
        .dark [class~="text-gray-600"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-700"] { color: #cbd5e1 !important; }
        .dark [class~="text-gray-800"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#0f1f45]"] { color: #f1f5f9 !important; }
        .dark [class*="text-[#00206E]"] { color: #93c5fd !important; }
        .dark [class~="text-blue-500"] { color: #60a5fa !important; }
        .dark [class~="text-blue-600"] { color: #60a5fa !important; }
        .dark [class~="text-red-500"] { color: #f87171 !important; }
        .dark [class~="text-red-600"] { color: #f87171 !important; }
        .dark [class~="text-emerald-600"] { color: #34d399 !important; }
        .dark [class~="text-emerald-700"] { color: #34d399 !important; }
        .dark [class~="text-amber-600"] { color: #fbbf24 !important; }
        .dark [class~="hover:text-gray-600"]:hover { color: #e2e8f0 !important; }
        .dark [class~="hover:text-blue-600"]:hover { color: #60a5fa !important; }

        .dark [class~="border-gray-50"] { border-color: #334155 !important; }
        .dark [class~="border-gray-100"] { border-color: #334155 !important; }
        .dark [class~="border-gray-200"] { border-color: #475569 !important; }
        .dark [class~="border-gray-300"] { border-color: #64748b !important; }
        .dark [class*="border-[#00206E]"] { border-color: #60a5fa !important; }
        .dark [class~="border-blue-100"] { border-color: rgba(96,165,250,0.3) !important; }
        .dark [class~="border-emerald-100"] { border-color: rgba(52,211,153,0.3) !important; }
        .dark [class~="border-red-100"] { border-color: rgba(248,113,113,0.3) !important; }
        .dark [class~="border-amber-100"] { border-color: rgba(251,191,36,0.3) !important; }
        .dark [class~="divide-gray-100"] > * + * { border-color: #334155 !important; }

        .dark input, .dark select, .dark textarea {
          background-color: #1e293b !important;
          color: #f1f5f9 !important;
          border-color: #475569 !important;
        }
        .dark input::placeholder, .dark textarea::placeholder { color: #64748b !important; }
      `}</style>

      {/* ── CLIENT PICKER ── */}
      {showForm && entryMode === 'client' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm">
          <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h4 className="text-base font-bold text-[#0f1f45] dark:text-slate-100">Select ERP Client</h4>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                  A company must be linked to an ERP client. Showing MCA-applicable clients — Private Limited, LLP, OPC, Section 8 etc.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="p-5">
            {clients.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400 dark:text-slate-500">
                No clients found in ERP. Add clients from the Clients module first.
              </p>
            ) : mcaClients.length === 0 && !showAllClients ? (
              <div className="py-6 text-center text-sm">
                <p className="text-gray-400 dark:text-slate-500 mb-3">
                  {clients.length} client{clients.length !== 1 ? 's' : ''} found in ERP, but none matched a
                  recognised MCA entity type (Private Limited, LLP, OPC, etc).
                </p>
                <button
                  type="button"
                  onClick={() => setShowAllClients(true)}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                  style={{ background: '#00206E' }}
                >
                  Show all {clients.length} ERP clients
                </button>
              </div>
            ) : (
              <div className="relative max-w-xl">
                <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">ERP Client</label>
                <input
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); setClientDropOpen(true); }}
                  onFocus={() => setClientDropOpen(true)}
                  onBlur={() => setTimeout(() => setClientDropOpen(false), 150)}
                  placeholder="Search client name, file number, PAN…"
                  className="w-full border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2.5 pr-9 text-sm focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
                  autoComplete="off"
                />
                <svg className="absolute right-3 top-[34px] text-gray-400 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>

                {clientDropOpen && (
                  <div className="cx-noscroll absolute z-30 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                    {filteredMcaClients.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-400 dark:text-slate-500">No clients match your search.</p>
                    ) : (
                      filteredMcaClients.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setClientDropOpen(false); handleClientSelect(c); }}
                          disabled={autoFetching}
                          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-slate-700 text-left transition disabled:opacity-50 border-t border-gray-50 dark:border-slate-700 first:border-t-0"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-200 truncate">{c.client_name}</p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 truncate">
                              {c.file_number && `${c.file_number} · `}
                              {CLIENT_TYPE_TO_COMPANY_TYPE[resolveMcaType(c.client_type)] || c.client_type || 'Type not set'}
                              {c.pan && ` · PAN: ${c.pan}`}
                            </p>
                          </div>
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium flex-shrink-0 ml-3">
                            {autoFetching ? 'Fetching…' : 'Select →'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1.5">
                  {filteredMcaClients.length} MCA-applicable client{filteredMcaClients.length !== 1 ? 's' : ''} available
                  {!showAllClients && clients.length > mcaClients.length && (
                    <> · <button type="button" onClick={() => setShowAllClients(true)} className="text-blue-600 dark:text-blue-400 hover:underline">show all {clients.length}</button></>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && entryMode === 'form' && (
        <form onSubmit={handleSave} className="relative bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg,#0f1f45,#00206E 55%,#FF8400)' }} />
          <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-3">
              {!editingId && (
                <button
                  type="button"
                  onClick={() => { setForm(EMPTY_FORM); setEntryMode('client'); }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition flex-shrink-0"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
              )}
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg,#0f1f45,#00206E)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-[15px] font-bold text-[#0f1f45] dark:text-slate-100">
                    {editingId ? 'Edit Company' : 'Add Company'}
                  </h4>
                  {form.client_id && !editingId && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900">
                      Auto-filled from ERP
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                  {editingId
                    ? 'Update company information. Directors & members are managed from the tabs inside the company.'
                    : 'Review auto-filled details from the ERP client and complete any missing fields.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 transition"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="p-6 space-y-8">
            <section>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1 h-4 rounded-full" style={{ background: '#00206E' }} />
                <p className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#0f1f45] dark:text-slate-100">Client &amp; Company Details</p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Linked Client *</label>
                  <div className={`${ic} bg-gray-50 dark:bg-slate-900 flex items-center justify-between`}>
                    <span className="truncate">
                      {form.client_id
                        ? (clients.find(c => String(c.id) === String(form.client_id))?.client_name || 'ERP client deleted')
                        : 'Not linked'}
                    </span>
                    {form.client_id && (
                      <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 ml-2 flex-shrink-0">ERP-linked</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Company Type</label>
                  <select
                    value={form.company_type}
                    onChange={e => setForm(f => ({ ...f, company_type: e.target.value }))}
                    className={ic}
                  >
                    <option>Private Limited Company</option>
                    <option>Public Limited Company</option>
                    <option>Section 8 Company</option>
                    <option>LLP</option>
                    <option>One Person Company</option>
                    <option>Producer Company</option>
                    <option>Nidhi Company</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">{isLLP ? 'LLPIN' : 'CIN'}</label>
                  <input
                    value={form.cin_llpin}
                    onChange={e => setForm(f => ({ ...f, cin_llpin: e.target.value.toUpperCase() }))}
                    placeholder={isLLP ? 'LLPIN' : 'CIN'}
                    className={ic}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">ROC</label>
                  {/* Type-to-filter combobox — a native <input list> against
                      a <datalist>, so typing "Kanpur" narrows the browser's
                      own suggestion list to matches instead of needing a
                      custom dropdown component. Still plain text underneath,
                      so anything not on the list can still be typed in full. */}
                  <input
                    list="roc-options"
                    value={form.roc}
                    onChange={e => setForm(f => ({ ...f, roc: e.target.value }))}
                    placeholder="Type to search ROC…"
                    className={ic}
                  />
                  <datalist id="roc-options">
                    {ROC_OPTIONS.map(r => <option key={r} value={r} />)}
                  </datalist>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Company / LLP Name *</label>
                  <input
                    required
                    value={form.company_name}
                    onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                    placeholder="Legal name as per MCA"
                    className={ic}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Incorporation Date</label>
                  <input
                    type="date"
                    value={form.incorporation_date}
                    onChange={e => setForm(f => ({ ...f, incorporation_date: e.target.value }))}
                    className={ic}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">GSTIN — Principal Place of Business</label>
                  <input
                    value={form.gstin}
                    onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                    placeholder="Principal-state GSTIN"
                    className={ic}
                  />
                </div>

                <div className="col-span-4">
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Additional State GST Registrations</label>
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-2">Same PAN, different state code — add each state's GSTIN here.</p>
                  <div className="grid grid-cols-4 gap-3">
                    <input value={gstDraft.state} onChange={e => setGstDraft(d => ({ ...d, state: e.target.value }))} placeholder="State (e.g. Maharashtra)" className={ic} />
                    <input value={gstDraft.gstin} onChange={e => setGstDraft(d => ({ ...d, gstin: e.target.value.toUpperCase() }))} placeholder="GSTIN for that state" className={`${ic} col-span-2`} />
                    <button type="button" onClick={addGstReg} className="px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-xs font-semibold text-[#00206E] dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition">＋ Add</button>
                  </div>
                  {(form.gst_registrations || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {(form.gst_registrations || []).map(g => (
                        <div key={g.id || g.gstin} className="flex items-center justify-between bg-gray-50 dark:bg-slate-900 rounded-lg px-3 py-2 text-xs border border-gray-100 dark:border-slate-700">
                          <span className="font-semibold text-gray-700 dark:text-slate-200">{g.state || '—'}<span className="text-gray-400 dark:text-slate-500 font-mono font-normal ml-2">{g.gstin}</span></span>
                          <button type="button" onClick={() => removeGstReg(g.id)} className="text-red-500 hover:underline flex-shrink-0 ml-3">Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-slate-700 pt-7">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1 h-4 rounded-full" style={{ background: '#7c3aed' }} />
                <p className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#0f1f45] dark:text-slate-100">Statutory IDs &amp; Capital</p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">PAN</label>
                  <input value={form.pan} onChange={e => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} className={ic} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">TAN</label>
                  <input value={form.tan} onChange={e => setForm(f => ({ ...f, tan: e.target.value.toUpperCase() }))} className={ic} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">ESIC No.</label>
                  <input value={form.esic} onChange={e => setForm(f => ({ ...f, esic: e.target.value }))} placeholder="17-digit ESIC code" className={ic} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">EPFO No.</label>
                  <input value={form.epfo} onChange={e => setForm(f => ({ ...f, epfo: e.target.value }))} placeholder="EPFO establishment code" className={ic} />
                </div>

                {!isLLP ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Authorized Capital</label>
                      <input type="number" value={form.authorized_capital} onChange={e => setForm(f => ({ ...f, authorized_capital: e.target.value }))} className={ic} />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Paid-up Capital</label>
                      <input type="number" value={form.paid_up_capital} onChange={e => setForm(f => ({ ...f, paid_up_capital: e.target.value }))} className={ic} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Total Contribution (as per LLP Agreement)</label>
                      <input type="number" value={form.llp_contribution} onChange={e => setForm(f => ({ ...f, llp_contribution: e.target.value }))} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">LLP Agreement Date</label>
                      <input type="date" value={form.date_of_agreement} onChange={e => setForm(f => ({ ...f, date_of_agreement: e.target.value }))} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 3 SRN</label>
                      <input value={form.form3_srn} onChange={e => setForm(f => ({ ...f, form3_srn: e.target.value.toUpperCase() }))} placeholder="Agreement filing SRN" className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Form 3 Filing Date</label>
                      <input type="date" value={form.form3_date} onChange={e => setForm(f => ({ ...f, form3_date: e.target.value }))} className={ic} />
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-slate-700 pt-7">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-1 h-4 rounded-full" style={{ background: '#0d9488' }} />
                <p className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#0f1f45] dark:text-slate-100">Registered Office</p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={ic} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Mobile</label>
                  <input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} className={ic} />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">Registered Office Address</label>
                  <input value={form.registered_office} onChange={e => setForm(f => ({ ...f, registered_office: e.target.value }))} className={ic} />
                </div>
              </div>

              {!isLLP && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 border border-gray-100 dark:border-slate-700 rounded-xl px-3 py-2.5">
                    <input type="checkbox" checked={form.is_listed} onChange={e => setForm(f => ({ ...f, is_listed: e.target.checked }))} />
                    <span>Listed Company</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 border border-gray-100 dark:border-slate-700 rounded-xl px-3 py-2.5">
                    <input type="checkbox" checked={form.has_subsidiaries} onChange={e => setForm(f => ({ ...f, has_subsidiaries: e.target.checked }))} />
                    <span>Has Subsidiaries / JV / Associate Companies</span>
                  </label>
                  <p className="text-[11px] text-gray-400 dark:text-slate-500 col-span-2 -mt-2">Drives Annual Filing form applicability (AOC-4 XBRL, AOC-4 CFS, MGT-8, Secretarial Audit).</p>
                </div>
              )}
            </section>

            <section className="border-t border-gray-100 dark:border-slate-700 pt-7">
              <div className="flex items-start gap-3 rounded-xl border border-blue-100 dark:border-blue-900/60 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#00206E" strokeWidth="2" className="flex-shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
                  {isLLP ? 'Designated Partners & Partners' : 'Directors & Members (Register of Members)'} are managed
                  from the tabs inside the company after it is saved — where you can link them to People Master and
                  sync them back to the ERP People module.
                </p>
              </div>
            </section>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 text-sm font-medium hover:bg-white dark:hover:bg-slate-700 transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold bg-[#0f1f45] hover:bg-[#162d5e] disabled:opacity-60 transition shadow-sm"
            >
              {saving ? 'Saving...' : editingId ? 'Update Company' : 'Save Company'}
            </button>
          </div>
        </form>
      )}

      <div className="relative bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: 'linear-gradient(90deg,#0f1f45,#00206E 55%,#FF8400)' }} />
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg,#0f1f45,#00206E)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
            </div>
            <div>
              <p className="text-[15px] font-bold text-[#0f1f45] dark:text-slate-100">Registered Companies</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">
                {companies.length} {companies.length === 1 ? 'company' : 'companies'} on record
                {(() => {
                  const tracked = new Set(companies.map(c => String(c.client_id)));
                  const untracked = mcaClients.filter(c => !tracked.has(String(c.id))).length;
                  return untracked > 0
                    ? <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-900 font-medium">{untracked} eligible client{untracked === 1 ? '' : 's'} with no company record</span>
                    : null;
                })()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, CIN, PAN..."
                className="pl-8 pr-4 py-2 w-72 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 bg-gray-50 dark:bg-slate-900 focus:bg-white transition"
              />
            </div>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition shadow-sm whitespace-nowrap" style={{ background: '#FF8400' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Company
              </button>
            )}
          </div>
        </div>

        {filteredCompanies.length === 0 ? (
          <div className="py-14 text-center text-gray-400 dark:text-slate-500 text-sm">
            No MCA company records yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'linear-gradient(90deg,#0f1f45,#13294f)' }}>
                {['Company', 'Client', 'Type', 'CIN / LLPIN', 'ROC', 'PAN', 'People', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em] first:rounded-tl-lg last:rounded-tr-lg" style={{ color: 'rgba(219,234,254,0.92)' }}>{h}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredCompanies.map(c => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-slate-700">
                  <td className="px-5 py-3">
                    <button
                      onClick={() => setSelectedCompany(c)}
                      className="text-sm font-semibold hover:underline text-left text-[#00206E]"
                    >
                      {c.company_name}
                    </button>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{c.registered_office || '-'}</p>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">{c.client_name || '-'}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">{c.company_type}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">{c.cin_llpin || '-'}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">{c.roc || '-'}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">{c.pan || '-'}</td>
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-slate-300">
                    {(Number(c.director_count) || 0) + (Number(c.member_count) || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap relative" data-kebab>
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === c.id ? null : c.id); }}
                      className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400 flex items-center justify-center"
                      data-kebab
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                      </svg>
                    </button>
                    {openMenuId === c.id && (
                      <div className="absolute right-0 top-10 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-xl z-[999] w-44 p-1.5" data-kebab>
                        <button
                          onClick={() => { setSelectedCompany(c); setOpenMenuId(null); }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 font-medium flex items-center gap-2">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          View Details
                        </button>
                        <button
                          onClick={() => { handleEdit(c); setOpenMenuId(null); }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 font-medium flex items-center gap-2">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Edit
                        </button>
                        <div className="border-t border-gray-100 dark:border-slate-700 my-1"/>
                        <button
                          onClick={() => { handleDelete(c); setOpenMenuId(null); }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 font-medium flex items-center gap-2">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}


export default function CompanyMasterPage(props) {
  return (
    <ToastProvider>
      <CompanyMaster {...props} />
    </ToastProvider>
  );
}